import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Reorder, useDragControls, motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { useTheme } from '../contexts/ThemeContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { api, buildTenantUrl } from '../config/api';
import {
  DocumentTextIcon,
  PencilSquareIcon,
  SparklesIcon,
  ArrowPathIcon,
  ChevronUpDownIcon,
  ServerIcon,
  VariableIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  FolderIcon,
  FolderOpenIcon,
  BookOpenIcon,
  PlayCircleIcon,
  PlayIcon,
  PencilIcon,
  SquaresPlusIcon,
  InformationCircleIcon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
  EllipsisHorizontalIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  DocumentDuplicateIcon,
  BuildingOffice2Icon,
  PhotoIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline';
import { Document, Page, pdfjs } from 'react-pdf';
import '../styles/assignment-studio.css';

// Same pdf.js worker as Dhyana / CourseStudio (served from public/).
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ── Icon wrapper ───────────────────────────────────────────────────────────
// Maps the design's lucide-style names → the nearest Heroicon so the JSX stays
// readable. `size` is in px; we render an inline svg.
type IconName =
  | 'file-pen-line' | 'sparkles' | 'target' | 'refresh-cw' | 'grip-vertical' | 'hard-drive'
  | 'sigma' | 'zap' | 'chevron-down' | 'chevron-up' | 'chevron-right' | 'chevron-left'
  | 'check-circle' | 'check' | 'alert-triangle' | 'users' | 'folder' | 'folder-open'
  | 'book-open' | 'play-circle' | 'play' | 'pen-tool' | 'pen-line' | 'shapes' | 'info'
  | 'clock' | 'gauge' | 'more-horizontal' | 'monitor' | 'smartphone' | 'type' | 'plus'
  | 'minus' | 'trash-2' | 'x' | 'arrow-left' | 'arrow-right' | 'eye' | 'search'
  | 'circle-help' | 'circle-dot' | 'copy' | 'image';

const ICONS: Record<IconName, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  'file-pen-line': PencilSquareIcon,
  sparkles: SparklesIcon,
  target: ViewfinderCircleIcon,
  'refresh-cw': ArrowPathIcon,
  'grip-vertical': ChevronUpDownIcon,
  'hard-drive': ServerIcon,
  sigma: VariableIcon,
  zap: BoltIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-up': ChevronUpIcon,
  'chevron-right': ChevronRightIcon,
  'chevron-left': ChevronLeftIcon,
  'check-circle': CheckCircleIcon,
  check: CheckIcon,
  'alert-triangle': ExclamationTriangleIcon,
  users: UsersIcon,
  folder: FolderIcon,
  'folder-open': FolderOpenIcon,
  'book-open': BookOpenIcon,
  'play-circle': PlayCircleIcon,
  play: PlayIcon,
  'pen-tool': PencilIcon,
  'pen-line': PencilIcon,
  shapes: SquaresPlusIcon,
  info: InformationCircleIcon,
  clock: ClockIcon,
  gauge: AdjustmentsHorizontalIcon,
  'more-horizontal': EllipsisHorizontalIcon,
  monitor: ComputerDesktopIcon,
  smartphone: DevicePhoneMobileIcon,
  type: DocumentTextIcon,
  plus: PlusIcon,
  minus: MinusIcon,
  'trash-2': TrashIcon,
  x: XMarkIcon,
  'arrow-left': ArrowLeftIcon,
  'arrow-right': ArrowRightIcon,
  eye: EyeIcon,
  search: MagnifyingGlassIcon,
  'circle-help': QuestionMarkCircleIcon,
  'circle-dot': PlayCircleIcon,
  copy: DocumentDuplicateIcon,
  image: PhotoIcon,
};

const Icon: React.FC<{ name: IconName; size?: number; className?: string; style?: React.CSSProperties }> = ({ name, size = 16, className, style }) => {
  const C = ICONS[name] || InformationCircleIcon;
  return <C width={size} height={size} className={className} style={{ width: size, height: size, flex: 'none', ...style }} aria-hidden />;
};

// ── Types (mirror backend schemas/assignment.py) ───────────────────────────
type GenerationStatus = 'pending' | 'planning' | 'plan_ready' | 'writing' | 'ready' | 'failed';
type AssignmentStatusField = 'draft' | 'saved';
type DifficultyBand = 'scaffold' | 'core' | 'stretch';

interface AssignmentSummary {
  id: string;
  ref_id: number;
  title: string;
  folder_id?: string | null;
  status: AssignmentStatusField;
  generation_status: GenerationStatus;
  difficulty_preset?: string | null;
  syllabus?: string | null;
  assignment_type?: string;
  targeting_enabled: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Past-paper precedent a question/plan row is modeled on (read-only provenance).
interface ExamRef {
  board: string;
  year?: string | null;
  session?: string | null;
  source_url?: string | null;
}

// One content block as the API returns it (statement_blocks / solution_blocks).
//  text    → { id, type:'text', md }
//  formula → { id, type:'formula', latex }
//  figure  → { id, type:'figure', spec:{figure_type, intent, notes?}, svg }
interface FigureSpec {
  figure_type?: string;
  intent?: string;
  notes?: string | null;
}
interface QBlock {
  id: string;
  type: 'text' | 'formula' | 'figure' | 'image';
  md?: string | null;
  latex?: string | null;
  spec?: FigureSpec | null;
  svg?: string | null;
  url?: string | null;     // image block: uploaded image URL
  caption?: string | null; // image block: optional caption
  // Tolerated legacy/flat fallbacks (agent QBlock emits these flat) — read-only.
  figure_type?: string | null;
  figure_intent?: string | null;
}

interface RankedConcept {
  concept: string;
  score?: number;
  n_students?: number;
  reasons?: string[];
}
interface WeaknessSnapshot {
  ranked_concepts?: RankedConcept[];
  signal_strength?: string | number | null;
  thin_signal?: boolean;
  source?: 'batch' | 'textbook_kg' | 'none' | null;
}
interface BlueprintQuestion {
  focus: string;
  rationale: string;
  target_concepts: string[];
  difficulty_band: DifficultyBand;
  marks?: number | null;
  figure_brief?: string | null;
  exam_refs?: ExamRef[] | null;
}
interface Blueprint {
  overview?: string;
  questions?: BlueprintQuestion[];
}
interface AssignmentQuestion {
  id: string;
  order_index: number;
  statement_blocks: QBlock[];
  solution_blocks: QBlock[];
  focus?: string | null;
  rationale?: string | null;
  marks?: number | null;
  target_concepts?: string[] | null;
  difficulty_band?: DifficultyBand | null;
  exam_refs?: ExamRef[] | null;
  solution_check?: { status: 'verified' | 'mismatch' | 'inconclusive' | 'error'; stated?: string; computed?: string } | null;
}
interface AssignmentDetail extends AssignmentSummary {
  source_prompt?: string | null;
  grounding_refs?: Record<string, unknown> | null;
  batch_weakness_snapshot?: WeaknessSnapshot | null;
  blueprint?: Blueprint | null;
  generation_error?: string | null;
  created_by_name?: string | null;
  target_group_id?: string | null;
  target_group_name?: string | null;
  questions: AssignmentQuestion[];
}

interface BookOption { id: string; title: string; author: string; cover_url?: string | null; processed?: boolean; }
interface BookChapter { number: string; name: string; page_start: number; page_end: number | null; }
interface BookStructure { id: string; title: string; author?: string | null; total_pages?: number | null; cover_url?: string | null; read_url?: string | null; chapters: BookChapter[]; }
interface VideoOption { id: string; title: string; thumbnail_url?: string | null; duration_seconds?: number | null; created_at?: string | null; source_type?: string | null; external_url?: string | null; }
interface DigestTopic { title: string; start_sec?: number | null; end_sec?: number | null; }
interface GroupOption { id: string; name: string; student_count?: number | null; }
interface VideoFolder { id: string; parent_id?: string | null; name: string; video_count?: number | null; subfolder_count?: number | null; }

// A grounding source the tutor has tagged in the create panel. Carries both the
// display chip (label/meta) and the API ref payload it emits on generate.
type TaggedSource =
  | { kind: 'book'; label: string; meta: string; ref: { book_id: string; chapter_number?: number; page_start: number; page_end: number } }
  | { kind: 'video'; label: string; meta: string; ref: { video_id: string; topic_index: number | null } };

// ── difficulty presets ─────────────────────────────────────────────────────
const DIFFS: { v: string; preset: 'foundation' | 'balanced' | 'challenge'; hint: string }[] = [
  { v: 'Foundation', preset: 'foundation', hint: 'Eased in — more scaffolded, single-step problems' },
  { v: 'Balanced', preset: 'balanced', hint: 'Exam-style mix of scaffold, core and stretch · recommended' },
  { v: 'Challenge', preset: 'challenge', hint: 'Fewer hints, tougher multi-step numericals' },
];
const presetToLabel = (p?: string | null) => DIFFS.find((d) => d.preset === p)?.v || 'Balanced';

// ── syllabus / exam-alignment options (mirror backend SYLLABUS_REGISTRY) ───
const SYLLABI: { value: string; label: string }[] = [
  { value: 'jee_main', label: 'JEE Main' },
  { value: 'jee_advanced', label: 'JEE Advanced' },
  { value: 'neet', label: 'NEET' },
  { value: 'cbse_x', label: 'CBSE X' },
  { value: 'cbse_xii', label: 'CBSE XII' },
  { value: 'icse', label: 'ICSE/ISC' },
  { value: 'igcse', label: 'IGCSE' },
];
const syllabusToLabel = (v?: string | null) => SYLLABI.find((s) => s.value === v)?.label || null;
const examRefText = (r: ExamRef) => [r.board, r.year, r.session].filter(Boolean).join(' · ');

const MAX_QUESTIONS = 10;
// Figure-type options = the real backend enum (schemas/assignment.py FigureSpecPayload).
const FIG_TYPES: { value: string; label: string }[] = [
  { value: 'fbd', label: 'FBD' },
  { value: 'ray_diagram', label: 'Ray diagram' },
  { value: 'circuit', label: 'Circuit' },
  { value: 'incline', label: 'Incline' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'projectile', label: 'Projectile' },
  { value: 'geometry', label: 'Geometry' },
  { value: 'vector', label: 'Vector' },
  { value: 'structural', label: 'Structural' },
  { value: 'other', label: 'Other' },
];
const figTypeLabel = (v?: string | null) => FIG_TYPES.find((f) => f.value === v)?.label || 'Figure';

const POLL_MS = 2500;
const ACTIVE_GEN: GenerationStatus[] = ['pending', 'planning', 'writing'];
const TEACHING_ROLES = ['institution_admin', 'institution_sub_admin', 'teacher'];

// ── helpers ────────────────────────────────────────────────────────────────
const relTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const fmtDur = (sec?: number | null): string => {
  if (!sec || sec <= 0) return '';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};
const fmtSpan = (a?: number | null, b?: number | null): string => `${fmtDur(a) || '0:00'} – ${fmtDur(b) || ''}`;

// difficulty-band badge palette (tinted, reads tokens for the neutral default)
const bandMeta = (band?: string | null): { label: string; c: string; bg: string; bd: string } => {
  switch (band) {
    case 'scaffold': return { label: 'Scaffold', c: '#2563EB', bg: 'rgba(59,130,246,0.12)', bd: 'rgba(59,130,246,0.28)' };
    case 'core': return { label: 'Core', c: 'var(--accent-primary-darker)', bg: 'rgba(6,182,212,0.12)', bd: 'rgba(6,182,212,0.28)' };
    case 'stretch': return { label: 'Stretch', c: 'var(--as-amber-700)', bg: 'rgba(216,139,48,0.14)', bd: 'rgba(216,139,48,0.30)' };
    default: return { label: band || 'Core', c: 'var(--text-secondary)', bg: 'var(--bg-tertiary)', bd: 'var(--border-color)' };
  }
};

// Figure block field accessors (tolerate the flat agent shape as a fallback).
const figIntent = (b: QBlock): string => b.spec?.intent ?? b.figure_intent ?? '';
const figType = (b: QBlock): string => b.spec?.figure_type ?? b.figure_type ?? 'other';
const figNotes = (b: QBlock): string | undefined => b.spec?.notes ?? undefined;
const isDegradedSvg = (svg?: string | null): boolean => !!svg && svg.includes("couldn't be drawn");
const newBlockId = () => `n${Date.now()}${Math.floor(Math.random() * 1000)}`;

// ── Spinner / BookCover (copied verbatim from CourseStudio) ─────────────────
const Spinner: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cx('animate-spin rounded-full border-b-2 border-cyan-500', className || 'h-8 w-8')} />
);

const BookCover: React.FC<{ url?: string | null; title?: string | null; className?: string }> = ({ url, title, className }) => {
  const [err, setErr] = useState(false);
  if (url && !err) {
    return <img src={url} alt={title || ''} onError={() => setErr(true)} className={cx('object-cover', className)} />;
  }
  return (
    <div className={cx('flex items-center justify-center bg-gradient-to-br from-cyan-700 via-cyan-500 to-sky-400 text-white/90', className)}>
      <BookOpenIcon className="h-1/3 w-1/3" />
    </div>
  );
};

// ── Hook: fetch a book's structure (cover, chapters, page count, read url) ──
function useBookStructure(bookId?: string | null) {
  const [data, setData] = useState<BookStructure | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!bookId) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .get(`/api/courses/book-structure/${bookId}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [bookId]);
  return { data, loading };
}

// ── PDF range viewer — react-pdf canvas preview (copied from CourseStudio) ──
const PdfRangeViewer: React.FC<{ url?: string | null; targetPage: number; minPage?: number; maxPage?: number; isDarkMode: boolean; fill?: boolean }> = ({ url, targetPage, minPage = 1, maxPage, isDarkMode, fill }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(targetPage);
  const [width, setWidth] = useState(360);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPage(targetPage);
    setFailed(false);
  }, [targetPage, url]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(200, el.clientWidth - 24));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [url, fill]);

  if (!url) {
    return (
      <div className={cx('flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed', fill ? 'h-full' : 'h-[380px]', isDarkMode ? 'border-slate-700 text-slate-500' : 'border-gray-200 text-gray-400')}>
        <BookOpenIcon className="h-8 w-8" />
        <span className="text-xs">Preview unavailable</span>
      </div>
    );
  }

  const upper = maxPage || numPages || targetPage;
  const safePage = Math.min(Math.max(page, 1), numPages || page);

  return (
    <div className={cx('flex flex-col gap-2', fill && 'h-full min-h-0')}>
      <div ref={ref} className={cx('relative overflow-auto rounded-xl bg-gray-100 ring-1 ring-black/5', fill ? 'min-h-0 flex-1' : 'h-[380px]')}>
        {failed ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-500">
            <BookOpenIcon className="h-8 w-8" />
            <span className="text-xs">Couldn't load preview</span>
          </div>
        ) : (
          <Document
            file={url}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={() => setFailed(true)}
            loading={<div className="flex h-full min-h-[300px] items-center justify-center"><Spinner /></div>}
            error={<div className="flex h-full min-h-[300px] items-center justify-center text-xs text-gray-500">Couldn't load preview</div>}
            className="flex justify-center py-2"
          >
            <Page pageNumber={safePage} width={width} renderTextLayer={false} renderAnnotationLayer={false} loading={<div className="flex h-[300px] items-center justify-center"><Spinner /></div>} />
          </Document>
        )}
      </div>
      <div className="flex flex-none items-center justify-center gap-3 text-xs">
        <button onClick={() => setPage((p) => Math.max(minPage, p - 1))} disabled={safePage <= minPage} className={cx('rounded-lg px-2.5 py-1 font-medium transition disabled:opacity-30', isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-600 hover:bg-gray-100')}>‹ Prev</button>
        <span className={cx('font-mono tabular-nums', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Page {safePage}{upper ? ` / ${upper}` : ''}</span>
        <button onClick={() => setPage((p) => Math.min(upper, p + 1))} disabled={safePage >= upper} className={cx('rounded-lg px-2.5 py-1 font-medium transition disabled:opacity-30', isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-600 hover:bg-gray-100')}>Next ›</button>
      </div>
    </div>
  );
};

// ── Math / markdown render (reuse the app's MarkdownRenderer pipeline) ──────
// A bare display formula → wrap in $$..$$ and feed to MarkdownRenderer
// (remark-math + rehype-katex). `not-prose` keeps the worksheet sheet tight.
const MathView: React.FC<{ latex: string; className?: string }> = ({ latex, className }) => (
  <div className={cx('not-prose', className)}><MarkdownRenderer content={`$$${(latex || '\\,').trim()}$$`} /></div>
);
// Markdown prose (supports **bold**, inline \(..\)) for text blocks + sheet.
const Prose: React.FC<{ md: string; className?: string }> = ({ md, className }) => (
  <MarkdownRenderer content={md || ''} className={className} />
);
// Render a backend figure svg fence (```vidya-svg```) via MarkdownRenderer.
const FigureSvg: React.FC<{ svg: string }> = ({ svg }) => (
  <div className="not-prose"><MarkdownRenderer content={svg} /></div>
);

// ── Icon button (token-themed, hover surface) ──────────────────────────────
const IconBtn: React.FC<{ name: IconName; onClick?: () => void; title?: string; size?: number; active?: boolean; disabled?: boolean; danger?: boolean; style?: React.CSSProperties }> = ({ name, onClick, title, size = 16, active, disabled, danger, style }) => {
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: active ? 'var(--bg-interactive-hover)' : h ? 'var(--bg-interactive)' : 'transparent',
        color: danger ? 'var(--as-danger)' : active ? 'var(--accent-primary-darker)' : h ? 'var(--text-primary)' : 'var(--text-tertiary)',
        opacity: disabled ? 0.5 : 1, transition: 'all .18s ease', flex: 'none', ...style,
      }}
    >
      <Icon name={name} size={size} />
    </button>
  );
};

// ── Segmented control ──────────────────────────────────────────────────────
type SegOption = { value: string; label?: string; icon?: IconName };
const Segmented: React.FC<{ options: (string | SegOption)[]; value: string; onChange: (v: string) => void; size?: 'sm' | 'md' }> = ({ options, value, onChange, size = 'md' }) => (
  <div style={{ display: 'inline-flex', background: 'var(--bg-tertiary)', borderRadius: 11, padding: 3, gap: 2 }}>
    {options.map((o) => {
      const opt: SegOption = typeof o === 'string' ? { value: o, label: o } : o;
      const on = opt.value === value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            font: 'inherit', fontSize: size === 'sm' ? 12 : 13, fontWeight: 600,
            border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: size === 'sm' ? '6px 12px' : '7px 16px', borderRadius: 8,
            background: on ? 'var(--bg-secondary)' : 'transparent',
            color: on ? 'var(--accent-primary-darker)' : 'var(--text-secondary)',
            boxShadow: on ? 'var(--shadow-sm)' : 'none', transition: 'all .18s ease',
          }}
        >
          {opt.icon && <Icon name={opt.icon} size={14} />}
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ── Toggle switch ──────────────────────────────────────────────────────────
const Toggle: React.FC<{ on: boolean; onChange: () => void }> = ({ on, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    role="switch"
    aria-checked={on}
    style={{
      width: 44, height: 26, borderRadius: 999, border: 'none', cursor: 'pointer', flex: 'none', padding: 3,
      background: on ? 'var(--accent-primary)' : 'var(--bg-tertiary)', transition: 'background .2s', position: 'relative',
      boxShadow: on ? 'inset 0 0 0 1px rgba(6,182,212,0.5)' : 'inset 0 0 0 1px var(--border-color)',
    }}
  >
    <span style={{ display: 'block', width: 20, height: 20, borderRadius: 999, background: '#fff', transform: on ? 'translateX(18px)' : 'translateX(0)', transition: 'transform .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
  </button>
);

// ── Status chip (from generation_status + status) ──────────────────────────
type ChipKind = 'generating' | 'ready' | 'draft' | 'saved' | 'failed';
const chipFor = (a: { generation_status: GenerationStatus; status: AssignmentStatusField }): ChipKind => {
  if (a.generation_status === 'failed') return 'failed';
  if (ACTIVE_GEN.includes(a.generation_status)) return 'generating';
  // plan_ready = stopped at the review gate (not actively generating) → a draft
  // awaiting the tutor's review before questions are written.
  if (a.generation_status === 'plan_ready') return 'draft';
  if (a.status === 'saved') return 'saved';
  return 'ready';
};
const AStatus: React.FC<{ kind: ChipKind; small?: boolean }> = ({ kind, small }) => {
  const map: Record<ChipKind, { label: string; c: string; bg: string; dot: string; pulse?: boolean }> = {
    generating: { label: 'Generating…', c: 'var(--accent-primary-darker)', bg: 'rgba(6,182,212,0.12)', dot: '#06B6D4', pulse: true },
    ready: { label: 'Ready', c: 'var(--accent-primary-darker)', bg: 'rgba(6,182,212,0.10)', dot: '#06B6D4' },
    draft: { label: 'Draft', c: 'var(--text-secondary)', bg: 'var(--bg-tertiary)', dot: '#9CA3AF' },
    saved: { label: 'Saved', c: 'var(--as-amber-700)', bg: 'rgba(216,139,48,0.13)', dot: 'var(--as-amber-600)' },
    failed: { label: 'Failed', c: 'var(--as-danger)', bg: 'rgba(220,38,38,0.10)', dot: '#DC2626' },
  };
  const s = map[kind];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: small ? 11.5 : 12, fontWeight: 600, padding: small ? '3px 9px' : '4px 11px', borderRadius: 999, color: s.c, background: s.bg }}>
      <span className={s.pulse ? 'as-pulse' : ''} style={{ width: 6, height: 6, borderRadius: 999, background: s.dot, display: 'inline-block' }} />
      {s.label}
    </span>
  );
};

// ── Marks stepper ──────────────────────────────────────────────────────────
const MarksStepper: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
    <span className="as-marks">
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))} title="Fewer marks"><Icon name="minus" size={13} /></button>
      <span style={{ minWidth: 20, textAlign: 'center', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <button type="button" onClick={() => onChange(Math.min(20, value + 1))} title="More marks"><Icon name="plus" size={13} /></button>
    </span>
    <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', fontWeight: 600 }}>marks</span>
  </span>
);

// ── Difficulty-band badge ──────────────────────────────────────────────────
const BandBadge: React.FC<{ band?: string | null }> = ({ band }) => {
  const m = bandMeta(band);
  return <span className="as-badge" style={{ background: m.bg, color: m.c, border: `1px solid ${m.bd}` }}>{m.label}</span>;
};

// ════════════════════════════════════════════════════════════════════════
//  Editable content blocks (text / formula / figure)
// ════════════════════════════════════════════════════════════════════════
const autoGrow = (el: HTMLTextAreaElement | null) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };

const BlockTools: React.FC<{ onDelete?: () => void; onMoveUp?: () => void; onMoveDown?: () => void }> = ({ onDelete, onMoveUp, onMoveDown }) => (
  <div className="as-eblock-tools">
    {onMoveUp && <IconBtn name="chevron-up" title="Move block up" size={13} onClick={onMoveUp} />}
    {onMoveDown && <IconBtn name="chevron-down" title="Move block down" size={13} onClick={onMoveDown} />}
    {onDelete && <IconBtn name="trash-2" title="Delete block" size={13} danger onClick={onDelete} />}
  </div>
);

const TextBlock: React.FC<{ block: QBlock; onChange: (b: QBlock) => void; onDelete?: () => void; onMoveUp?: () => void; onMoveDown?: () => void }> = ({ block, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(block.md || '');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); autoGrow(ref.current); } }, [editing]);
  useEffect(() => { if (!editing) setVal(block.md || ''); }, [block.md, editing]);
  const commit = () => { setEditing(false); if (val !== (block.md || '')) onChange({ ...block, md: val }); };
  return (
    <div className={cx('as-eblock', editing && 'editing')} onClick={() => !editing && setEditing(true)}>
      <span className="as-eblock-tag">Text · markdown</span>
      <BlockTools onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
      {editing ? (
        <textarea
          ref={ref}
          className="as-ta"
          value={val}
          style={{ fontSize: 13.5 }}
          onChange={(e) => { setVal(e.target.value); autoGrow(e.target); }}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') { setVal(block.md || ''); setEditing(false); } }}
        />
      ) : (
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-primary)' }}><Prose md={block.md || '_Empty — click to edit._'} /></div>
      )}
    </div>
  );
};

const FormulaBlock: React.FC<{ block: QBlock; onChange: (b: QBlock) => void; onDelete?: () => void; onMoveUp?: () => void; onMoveDown?: () => void }> = ({ block, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [latex, setLatex] = useState(block.latex || '');
  const editingRef = useRef(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { autoGrow(ref.current); }, []);
  // Sync external updates (regen) only while NOT actively editing — never fight the caret.
  useEffect(() => { if (!editingRef.current) setLatex(block.latex || ''); }, [block.latex]);
  const commit = () => { editingRef.current = false; if (latex !== (block.latex || '')) onChange({ ...block, latex }); };
  return (
    <div className="as-eblock editing" style={{ cursor: 'auto' }}>
      <span className="as-eblock-tag" style={{ opacity: 1 }}>Formula · LaTeX</span>
      <BlockTools onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 9, marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Icon name="sigma" size={15} style={{ color: 'var(--accent-primary-darker)', marginTop: 8 }} />
          <textarea
            ref={ref}
            className="as-latex"
            value={latex}
            rows={1}
            spellCheck={false}
            onFocus={() => { editingRef.current = true; }}
            onChange={(e) => { setLatex(e.target.value); autoGrow(e.target); }}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Escape') { setLatex(block.latex || ''); editingRef.current = false; (e.target as HTMLTextAreaElement).blur(); } }}
          />
        </div>
        <div className="as-formula-prev">
          <MathView latex={latex || '\\,'} />
        </div>
      </div>
    </div>
  );
};

const FigureBlock: React.FC<{
  block: QBlock;
  onChange: (b: QBlock) => void;
  onDelete?: () => void;
  onRegenerate?: (figure_type: string, intent: string, notes?: string) => Promise<void>;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}> = ({ block, onChange, onDelete, onRegenerate, onMoveUp, onMoveDown }) => {
  const degraded = isDegradedSvg(block.svg);
  const [open, setOpen] = useState(degraded);
  const [regen, setRegen] = useState(false);
  const [desc, setDesc] = useState(figIntent(block));
  const [ftype, setFtype] = useState(figType(block));
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (open) autoGrow(ref.current); }, [open]);
  useEffect(() => { setDesc(figIntent(block)); setFtype(figType(block)); }, [block]);

  const regenerate = async () => {
    if (!onRegenerate) return;
    setRegen(true);
    try { await onRegenerate(ftype, desc, figNotes(block)); } finally { setRegen(false); }
  };

  return (
    <div className="as-eblock" style={{ cursor: 'auto' }}>
      <span className="as-eblock-tag" style={{ opacity: 1 }}>Figure · {figTypeLabel(ftype)}</span>
      <BlockTools onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />

      {regen ? (
        <div className="as-wsfig" style={{ padding: 0, height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, flexDirection: 'column' }}>
          <Icon name="refresh-cw" size={20} className="as-spin" style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>Drawing diagram…</span>
        </div>
      ) : degraded ? (
        <div className="as-figfail">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon name="alert-triangle" size={16} style={{ color: 'var(--as-amber-600)' }} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--as-amber-700)' }}>Couldn't draw this diagram</span>
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Edit the description below and retry — be specific about objects, vectors and labels.</p>
          <div style={{ fontSize: 11.5, color: 'var(--text-primary)', background: 'var(--bg-secondary)', borderRadius: 8, padding: '9px 11px', border: '1px solid rgba(216,139,48,0.3)', lineHeight: 1.5, fontStyle: 'italic' }}>&ldquo;{desc}&rdquo;</div>
        </div>
      ) : block.svg ? (
        <div className="as-wsfig" style={{ padding: '10px 12px' }}>
          <FigureSvg svg={block.svg} />
        </div>
      ) : (
        <div className="as-wsfig" style={{ padding: 0, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, flexDirection: 'column' }}>
          <Icon name="shapes" size={20} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>Diagram pending — describe it below and draw.</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setOpen(!open)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '6px 10px', borderRadius: 9, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
          <Icon name="pen-tool" size={13} /> How this diagram should look
          <Icon name="chevron-down" size={13} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        <button type="button" onClick={regenerate} disabled={regen || !onRegenerate} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12, fontWeight: 600, cursor: regen ? 'default' : 'pointer', padding: '6px 10px', borderRadius: 9, border: '1px solid rgba(6,182,212,0.3)', background: 'rgba(6,182,212,0.06)', color: 'var(--accent-primary-darker)', opacity: !onRegenerate ? 0.5 : 1 }}>
          <Icon name="refresh-cw" size={13} className={regen ? 'as-spin' : ''} /> Regenerate diagram
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <span className="as-fl">Plain-English description</span>
            <textarea
              ref={ref}
              className="as-figdesc"
              value={desc}
              rows={2}
              onChange={(e) => { setDesc(e.target.value); autoGrow(e.target); }}
              onBlur={() => { if (desc !== figIntent(block)) onChange({ ...block, spec: { ...(block.spec || {}), figure_type: ftype, intent: desc } }); }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
            <span className="as-fl" style={{ marginBottom: 0, marginTop: 6 }}>Figure type</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FIG_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => { setFtype(t.value); onChange({ ...block, spec: { ...(block.spec || {}), figure_type: t.value, intent: desc } }); }}
                  style={{ font: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', padding: '5px 11px', borderRadius: 999, border: `1px solid ${ftype === t.value ? 'var(--accent-primary)' : 'var(--border-color)'}`, background: ftype === t.value ? 'rgba(6,182,212,0.1)' : 'transparent', color: ftype === t.value ? 'var(--accent-primary-darker)' : 'var(--text-secondary)', transition: 'all .14s' }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ImageBlock: React.FC<{ block: QBlock; onChange: (b: QBlock) => void; onDelete?: () => void; onMoveUp?: () => void; onMoveDown?: () => void }> = ({ block, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [caption, setCaption] = useState(block.caption || '');
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { setCaption(block.caption || ''); }, [block.caption]);
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);
  return (
    <div className="as-eblock" style={{ cursor: 'auto' }}>
      <span className="as-eblock-tag" style={{ opacity: 1 }}>Image · uploaded</span>
      <BlockTools onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
      <div className="as-wsfig" style={{ padding: 10, display: 'flex', justifyContent: 'center' }}>
        {block.url ? (
          <button type="button" onClick={() => setExpanded(true)} title="Click to expand" className="group" style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'zoom-in', position: 'relative', maxWidth: '100%' }}>
            <img src={block.url} alt={caption || 'uploaded figure'} style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 6, display: 'block' }} />
            <span className="opacity-0 group-hover:opacity-100" style={{ position: 'absolute', top: 8, right: 8, fontSize: 11, fontWeight: 700, padding: '4px 9px', borderRadius: 7, background: 'rgba(15,23,42,0.78)', color: '#fff', transition: 'opacity .15s', pointerEvents: 'none' }}>⤢ Click to expand</span>
          </button>
        ) : <span style={{ fontSize: 12, color: '#6B7280', padding: 24 }}>Image missing</span>}
      </div>
      {expanded && block.url && createPortal(
        <div onClick={() => setExpanded(false)} style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, background: 'rgba(3,7,18,0.82)', backdropFilter: 'blur(4px)', cursor: 'zoom-out' }}>
          <img src={block.url} alt={caption || 'uploaded figure'} style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 10, boxShadow: '0 30px 90px -20px rgba(0,0,0,0.8)' }} />
          {caption && <span style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', fontSize: 13, color: 'rgba(255,255,255,0.85)', background: 'rgba(15,23,42,0.7)', padding: '6px 14px', borderRadius: 9 }}>{caption}</span>}
        </div>,
        document.body
      )}
      <input
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        onBlur={() => { if (caption !== (block.caption || '')) onChange({ ...block, caption }); }}
        placeholder="Caption (optional — shown under the image)"
        className="as-inp"
        style={{ marginTop: 8, fontSize: 12.5, padding: '8px 11px' }}
      />
    </div>
  );
};

const ContentBlock: React.FC<{
  block: QBlock;
  onChange: (b: QBlock) => void;
  onDelete?: () => void;
  onRegenerate?: (figure_type: string, intent: string, notes?: string) => Promise<void>;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}> = ({ block, onChange, onDelete, onRegenerate, onMoveUp, onMoveDown }) => {
  if (block.type === 'text') return <TextBlock block={block} onChange={onChange} onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />;
  if (block.type === 'formula') return <FormulaBlock block={block} onChange={onChange} onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />;
  if (block.type === 'figure') return <FigureBlock block={block} onChange={onChange} onDelete={onDelete} onRegenerate={onRegenerate} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />;
  if (block.type === 'image') return <ImageBlock block={block} onChange={onChange} onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />;
  return null;
};

const AddBlockBar: React.FC<{ onAdd: (kind: QBlock['type']) => void }> = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const opts: { kind: QBlock['type']; icon: IconName; label: string }[] = [
    { kind: 'text', icon: 'type', label: 'Text' },
    { kind: 'formula', icon: 'sigma', label: 'Formula' },
    { kind: 'figure', icon: 'shapes', label: 'Figure' },
    { kind: 'image', icon: 'image', label: 'Image' },
  ];
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="as-addblock" onClick={() => setOpen(!open)}><Icon name="plus" size={14} /> Add block</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
          <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', zIndex: 21, display: 'flex', gap: 4, padding: 6, borderRadius: 12, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', boxShadow: '0 14px 36px -16px rgba(15,23,42,0.3)' }}>
            {opts.map((o) => (
              <button
                key={o.kind}
                type="button"
                onClick={() => { onAdd(o.kind); setOpen(false); }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, font: 'inherit', cursor: 'pointer', padding: '9px 14px', borderRadius: 9, border: 'none', background: 'transparent', color: 'var(--text-secondary)', transition: 'all .14s', minWidth: 58 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-interactive)'; e.currentTarget.style.color = 'var(--accent-primary-darker)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                <Icon name={o.icon} size={17} /><span style={{ fontSize: 11.5, fontWeight: 600 }}>{o.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Modal shell — one overlay (blur, click-out, Esc), header / body / footer
// ════════════════════════════════════════════════════════════════════════
const Modal: React.FC<{ title: string; subtitle?: string; width: number; onClose: () => void; footer?: React.ReactNode; children: React.ReactNode }> = ({ title, subtitle, width, onClose, footer, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="as-modal-overlay" onClick={onClose}>
      <div className="as-modal" style={{ maxWidth: width }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '20px 22px 14px', borderBottom: '1px solid var(--border-color)', flex: 'none' }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>{title}</h3>
            {subtitle && <p style={{ margin: '5px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{subtitle}</p>}
          </div>
          <IconBtn name="x" title="Close (Esc)" onClick={onClose} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
        {footer && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--border-color)', flex: 'none' }}>{footer}</div>
        )}
      </div>
    </div>
  );
};

const ModalBtn: React.FC<{ variant: 'ghost' | 'primary'; icon?: IconName; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({ variant, icon, disabled, onClick, children }) => {
  const { isDarkMode } = useTheme();
  if (variant === 'primary') {
    return (
      <button type="button" className="as-genbtn" onClick={onClick} disabled={disabled} style={{ width: 'auto', padding: '10px 18px', fontSize: 13.5, borderRadius: 11 }}>
        {icon && <Icon name={icon} size={15} />}{children}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx('inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition disabled:opacity-50', isDarkMode ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50')}
    >
      {icon && <Icon name={icon} size={15} />}{children}
    </button>
  );
};

// ── Book picker — Course Studio parity: list → opened book w/ PDF + TOC ─────
const BookPickerModal: React.FC<{ institutionId: string; onClose: () => void; onAdd: (s: TaggedSource) => void }> = ({ institutionId, onClose, onAdd }) => {
  const { isDarkMode } = useTheme();
  const [books, setBooks] = useState<BookOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);

  const { data: s, loading: structLoading } = useBookStructure(pickedId);
  const [chaps, setChaps] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(20);
  const [previewPage, setPreviewPage] = useState(1);

  useEffect(() => {
    api.get(`/api/institutions/${institutionId}/books`)
      .then((r) => setBooks(r.data.map((b: any) => ({ id: b.id, title: b.title, author: b.author, cover_url: b.cover_url, processed: !!b.processed }))))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, [institutionId]);

  // Reset selection state when a different book is opened.
  useEffect(() => { setChaps(new Set()); setFrom(1); setTo(20); setPreviewPage(1); }, [pickedId]);
  useEffect(() => { if (s?.total_pages) setTo(Math.min(20, s.total_pages)); }, [s]);

  const book = books.find((b) => b.id === pickedId) || null;
  const toggleChapter = (c: BookChapter) => {
    setChaps((p) => { const n = new Set(p); n.has(c.number) ? n.delete(c.number) : n.add(c.number); return n; });
    setPreviewPage(c.page_start);
  };

  // On Add: emit one ref per tagged chapter (so each chapter grounds independently),
  // mirroring how CourseStudio's BookInspectorPane maps chapters → ranges. With no
  // chapter selected, emit one ref for the custom page range.
  const add = () => {
    if (!book) return;
    const picked = (s?.chapters || []).filter((c) => chaps.has(c.number));
    if (picked.length > 0) {
      picked.forEach((c, i) => {
        const chNum = Number(c.number);
        const pe = c.page_end ?? c.page_start;
        onAdd({
          kind: 'book',
          label: `${book.title} — Ch. ${c.number} · ${c.name}`,
          meta: `pp. ${c.page_start}–${pe}`,
          ref: { book_id: book.id, ...(Number.isFinite(chNum) ? { chapter_number: chNum } : {}), page_start: c.page_start, page_end: pe },
        });
        void i;
      });
    } else {
      onAdd({ kind: 'book', label: book.title, meta: `pp. ${from}–${to}`, ref: { book_id: book.id, page_start: from, page_end: to } });
    }
    onClose();
  };

  const filtered = books.filter((b) => `${b.title} ${b.author}`.toLowerCase().includes(query.toLowerCase()));
  const selectedCount = chaps.size;

  const footer = (
    <>
      <ModalBtn variant="ghost" onClick={onClose}>Cancel</ModalBtn>
      <ModalBtn variant="primary" icon="plus" disabled={!book} onClick={add}>{selectedCount > 1 ? `Add ${selectedCount} chapters` : 'Add source'}</ModalBtn>
    </>
  );

  return (
    <Modal title="Add a book" subtitle="Pick a book, then tag chapters or a page range to ground questions in." width={760} onClose={onClose} footer={footer}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 380 }}>
        {/* LEFT — book library list (search + cover thumbs + "No summaries yet") */}
        <div style={{ borderRight: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--border-color)' }}>
            <Icon name="book-open" size={15} style={{ color: 'var(--accent-primary-darker)' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Your library</span>
          </div>
          <div style={{ padding: '12px 14px 0' }}>
            <div style={{ position: 'relative' }}>
              <Icon name="search" size={15} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-tertiary)' }} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search books…" className="as-inp" style={{ paddingLeft: 32, fontSize: 13 }} />
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner /></div>
            ) : filtered.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '20px 4px', textAlign: 'center' }}>{books.length === 0 ? 'No books in this institution yet.' : 'No books found.'}</div>
            ) : (
              filtered.map((b) => (
                <button key={b.id} type="button" onClick={() => setPickedId(b.id)} className="as-bookrow" style={{ borderColor: pickedId === b.id ? 'rgba(6,182,212,0.5)' : 'var(--border-color)', background: pickedId === b.id ? 'rgba(6,182,212,0.06)' : 'var(--bg-secondary)' }}>
                  <BookCover url={b.cover_url} title={b.title} className={cx('h-14 w-10 flex-none rounded shadow-sm', !b.processed && 'opacity-60')} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.title}</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.author}</span>
                    {!b.processed && (
                      <span className="mt-1 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400" title="This book has no page summaries yet — questions grounded in it may be thin until it's processed.">
                        No summaries yet
                      </span>
                    )}
                  </span>
                  {pickedId === b.id && <Icon name="check-circle" size={18} style={{ color: '#06B6D4' }} />}
                </button>
              ))
            )}
          </div>
        </div>

        {/* RIGHT — the opened book: PDF preview + chapter TOC + custom range */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {!book ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'var(--text-tertiary)', gap: 8, padding: 24 }}>
              <Icon name="book-open" size={26} />
              <span style={{ fontSize: 12.5 }}>Choose a book to open it</span>
            </div>
          ) : structLoading ? (
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spinner /></div>
          ) : (
            <>
              <div style={{ padding: '14px 14px 0' }}>
                <PdfRangeViewer url={s?.read_url} targetPage={previewPage} minPage={1} maxPage={s?.total_pages || undefined} isDarkMode={isDarkMode} />
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
                {s?.chapters && s.chapters.length > 0 ? (
                  <>
                    <div className="as-fl">Tag chapters</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {s.chapters.map((c) => {
                        const on = chaps.has(c.number);
                        return (
                          <button key={c.number} type="button" onClick={() => toggleChapter(c)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer', padding: '9px 11px', borderRadius: 10, border: `1px solid ${on ? 'rgba(6,182,212,0.5)' : 'var(--border-color)'}`, background: on ? 'rgba(6,182,212,0.08)' : 'var(--bg-secondary)', transition: 'all .14s' }}>
                            <span style={{ width: 19, height: 19, borderRadius: 6, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${on ? 'var(--accent-primary)' : 'var(--border-color)'}`, background: on ? 'var(--accent-primary)' : 'transparent', color: '#fff' }}>
                              {on && <Icon name="check" size={12} />}
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Ch. {c.number} · {c.name}</span>
                            </span>
                            <span style={{ fontFamily: 'var(--as-mono)', fontSize: 11.5, color: 'var(--text-secondary)' }}>pp. {c.page_start}{c.page_end ? `–${c.page_end}` : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginBottom: 4 }}>This book has no chapter map — set a page range below.</div>
                )}
                <div style={{ marginTop: 16, padding: 12, borderRadius: 11, border: '1px solid var(--border-color)', opacity: selectedCount > 0 ? 0.45 : 1 }}>
                  <div className="as-fl">Or a custom page range</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="number" min={1} value={from} disabled={selectedCount > 0} onChange={(e) => setFrom(Math.max(1, +e.target.value))} className="as-inp" style={{ width: 84, padding: '7px 9px' }} />
                    <Icon name="arrow-right" size={14} style={{ color: 'var(--text-tertiary)' }} />
                    <input type="number" min={from} value={to} disabled={selectedCount > 0} onChange={(e) => setTo(+e.target.value)} className="as-inp" style={{ width: 84, padding: '7px 9px' }} />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ── Lecture picker — video list (16:9 thumbs) → digest topics ──────────────
const youTubeId = (url?: string | null): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};
const videoThumb = (v: VideoOption): string | null => {
  if (v.thumbnail_url) return v.thumbnail_url;
  const yt = v.source_type === 'youtube' ? youTubeId(v.external_url) : null;
  return yt ? `https://img.youtube.com/vi/${yt}/hqdefault.jpg` : null;
};

// 16:9 poster — thumbnail or dark gradient + PlayIcon fallback, with a duration chip.
const VideoPoster: React.FC<{ v: VideoOption; w: number }> = ({ v, w }) => {
  const thumb = videoThumb(v);
  return (
    <span style={{ width: w, height: Math.round((w * 9) / 16), borderRadius: 9, flex: 'none', position: 'relative', overflow: 'hidden', display: 'block', background: thumb ? '#0F172A' : 'linear-gradient(135deg, #0F172A, #1E293B)', border: '1px solid var(--border-color)' }}>
      {thumb ? (
        <img src={thumb} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#22D3EE' }}><Icon name="play" size={18} /></span>
      )}
      {v.duration_seconds ? <span style={{ position: 'absolute', bottom: 3, right: 4, fontSize: 9, fontFamily: 'var(--as-mono)', color: 'rgba(255,255,255,0.9)', background: 'rgba(0,0,0,0.55)', padding: '0 4px', borderRadius: 3 }}>{fmtDur(v.duration_seconds)}</span> : null}
    </span>
  );
};

const TopicRow: React.FC<{ on: boolean; onClick: () => void; title: string; span: string; icon: IconName }> = ({ on, onClick, title, span, icon }) => (
  <button type="button" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer', padding: '10px 12px', borderRadius: 11, border: `1px solid ${on ? 'rgba(6,182,212,0.5)' : 'var(--border-color)'}`, background: on ? 'rgba(6,182,212,0.08)' : 'var(--bg-secondary)', transition: 'all .14s' }}>
    <span style={{ width: 18, height: 18, borderRadius: 999, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${on ? 'var(--accent-primary)' : 'var(--border-color)'}`, background: on ? 'var(--accent-primary)' : 'transparent' }}>
      {on && <span style={{ width: 7, height: 7, borderRadius: 999, background: '#fff' }} />}
    </span>
    <Icon name={icon} size={15} style={{ color: on ? 'var(--accent-primary-darker)' : 'var(--text-tertiary)' }} />
    <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
    <span style={{ fontFamily: 'var(--as-mono)', fontSize: 11.5, color: 'var(--text-secondary)' }}>{span}</span>
  </button>
);

const LecturePickerModal: React.FC<{ onClose: () => void; onAdd: (s: TaggedSource) => void }> = ({ onClose, onAdd }) => {
  const [videos, setVideos] = useState<VideoOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [vidId, setVidId] = useState<string | null>(null);
  const [topics, setTopics] = useState<DigestTopic[] | null>(null);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [sel, setSel] = useState<number | 'whole'>('whole');

  useEffect(() => {
    api.get('/api/videos')
      .then((r) => setVideos(r.data.map((v: any) => ({ id: v.id, title: v.title, thumbnail_url: v.thumbnail_url, duration_seconds: v.duration_seconds, created_at: v.created_at, source_type: v.source_type, external_url: v.external_url }))))
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!vidId) { setTopics(null); return; }
    let alive = true;
    setTopicsLoading(true);
    setSel('whole');
    api.get(`/api/videos/${vidId}`, { params: { with_digest: true } })
      .then((r) => { if (alive) setTopics((r.data?.lecture_digest?.topics as DigestTopic[]) || []); })
      .catch(() => alive && setTopics([]))
      .finally(() => alive && setTopicsLoading(false));
    return () => { alive = false; };
  }, [vidId]);

  const vid = videos.find((v) => v.id === vidId) || null;
  const add = () => {
    if (!vid) return;
    if (sel === 'whole') {
      onAdd({ kind: 'video', label: vid.title, meta: `whole · ${fmtDur(vid.duration_seconds) || '—'}`, ref: { video_id: vid.id, topic_index: null } });
    } else {
      const t = (topics || [])[sel];
      onAdd({ kind: 'video', label: `${vid.title} — ${t?.title || 'topic'}`, meta: fmtSpan(t?.start_sec, t?.end_sec), ref: { video_id: vid.id, topic_index: sel } });
    }
    onClose();
  };

  const footer = (
    <>
      <ModalBtn variant="ghost" onClick={onClose}>Cancel</ModalBtn>
      <ModalBtn variant="primary" icon="plus" disabled={!vid} onClick={add}>Add source</ModalBtn>
    </>
  );

  return (
    <Modal title="Add a lecture" subtitle="Pick a recording, then ground questions in the whole class or one topic." width={640} onClose={onClose} footer={footer}>
      <div style={{ padding: 18 }}>
        {!vid ? (
          loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner /></div>
          ) : videos.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '12px 4px' }}>No recorded lectures yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div className="as-fl">Recorded lectures</div>
              {videos.map((v) => (
                <button key={v.id} type="button" onClick={() => setVidId(v.id)} className="as-bookrow" style={{ padding: '11px 12px' }}>
                  <VideoPoster v={v} w={84} />
                  <span style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.title}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11.5, color: 'var(--text-secondary)' }}>
                      {v.duration_seconds ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="clock" size={11} style={{ opacity: 0.7 }} />{fmtDur(v.duration_seconds)}</span> : null}
                      {v.created_at ? <span>{relTime(v.created_at)}</span> : null}
                    </span>
                  </span>
                  <Icon name="chevron-right" size={16} style={{ color: 'var(--text-tertiary)' }} />
                </button>
              ))}
            </div>
          )
        ) : (
          <>
            <button type="button" onClick={() => setVidId(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 12 }}>
              <Icon name="arrow-left" size={14} /> All lectures
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 16 }}>
              <VideoPoster v={vid} w={92} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{vid.title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{fmtDur(vid.duration_seconds) || '—'}{vid.created_at ? ` · ${relTime(vid.created_at)}` : ''}</div>
              </div>
            </div>
            <div className="as-fl">Ground in</div>
            {topicsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <TopicRow on={sel === 'whole'} onClick={() => setSel('whole')} title="Whole lecture" span={fmtDur(vid.duration_seconds) || '—'} icon="play-circle" />
                {(topics || []).map((t, i) => (
                  <TopicRow key={i} on={sel === i} onClick={() => setSel(i)} title={t.title} span={fmtSpan(t.start_sec, t.end_sec)} icon="circle-dot" />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

// ── Save to Drive — LAZY per-level folder tree ─────────────────────────────
// Fetch /api/videos/folders (root) then /api/videos/folders?parent_id=<id> on
// expand. `subfolder_count` tells us whether a node is expandable without a fetch.
interface TreeNode extends VideoFolder { depth: number; }
// ── Target-batch picker modal (teacher's institution groups) ───────────────
const TargetBatchModal: React.FC<{ institutionId: string; selectedId: string | null; onClose: () => void; onPick: (g: GroupOption | null) => void }> = ({ institutionId, selectedId, onClose, onPick }) => {
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/api/institutions/${institutionId}/groups`)
      .then((r) => setGroups((r.data || []).map((g: any) => ({ id: g.id, name: g.name, student_count: g.student_count ?? g.member_count }))))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [institutionId]);
  return (
    <Modal title="Target a batch" subtitle="Weight the assignment to where this class actually struggled. Pick the group to profile." width={460} onClose={onClose}>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
        ) : groups.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '12px 4px' }}>No classes in this institution yet.</div>
        ) : (
          groups.map((g) => {
            const on = g.id === selectedId;
            return (
              <button key={g.id} type="button" onClick={() => { onPick(g); onClose(); }} className="as-bookrow" style={{ borderColor: on ? 'rgba(6,182,212,0.5)' : 'var(--border-color)', background: on ? 'rgba(6,182,212,0.06)' : 'var(--bg-secondary)' }}>
                <Icon name="users" size={17} style={{ color: 'var(--accent-primary-darker)' }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{g.name}</span>
                {typeof g.student_count === 'number' && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{g.student_count} students</span>}
                {on && <Icon name="check-circle" size={17} style={{ color: '#06B6D4' }} />}
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
};

const SaveToDriveModal: React.FC<{ onClose: () => void; onSave: (folderId: string, name: string) => void }> = ({ onClose, onSave }) => {
  const [rootLoading, setRootLoading] = useState(true);
  const [childrenById, setChildrenById] = useState<Record<string, VideoFolder[]>>({});
  const [root, setRoot] = useState<VideoFolder[]>([]);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<VideoFolder | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<VideoFolder[]>('/api/videos/folders')
      .then((r) => setRoot(r.data || []))
      .catch(() => setRoot([]))
      .finally(() => setRootLoading(false));
  }, []);

  const expand = useCallback(async (f: VideoFolder) => {
    setOpen((p) => { const n = new Set(p); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; });
    if (childrenById[f.id]) return;             // already fetched this level
    setLoadingIds((p) => new Set(p).add(f.id));
    try {
      const r = await api.get<VideoFolder[]>('/api/videos/folders', { params: { parent_id: f.id } });
      setChildrenById((m) => ({ ...m, [f.id]: r.data || [] }));
    } catch {
      setChildrenById((m) => ({ ...m, [f.id]: [] }));
    } finally {
      setLoadingIds((p) => { const n = new Set(p); n.delete(f.id); return n; });
    }
  }, [childrenById]);

  // Flatten the currently-expanded tree into indentation-aware rows.
  const rows = useMemo<TreeNode[]>(() => {
    const out: TreeNode[] = [];
    const walk = (level: VideoFolder[], depth: number) => {
      level.forEach((f) => {
        out.push({ ...f, depth });
        if (open.has(f.id)) walk(childrenById[f.id] || [], depth + 1);
      });
    };
    walk(root, 0);
    return out;
  }, [root, childrenById, open]);

  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try { await Promise.resolve(onSave(sel.id, sel.name)); onClose(); } finally { setSaving(false); }
  };

  const footer = (
    <>
      <ModalBtn variant="ghost" onClick={onClose}>Cancel</ModalBtn>
      <ModalBtn variant="primary" icon="hard-drive" disabled={!sel || saving} onClick={save}>Save here</ModalBtn>
    </>
  );

  return (
    <Modal title="Save to Library" subtitle="Filing this assignment into a shared folder makes it visible to that folder's students." width={540} onClose={onClose} footer={footer}>
      <div style={{ padding: 18 }}>
        <div className="as-fl">Choose a folder</div>
        <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, padding: 6, maxHeight: 300, overflowY: 'auto' }}>
          {rootLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
          ) : rows.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '16px 10px' }}>No folders yet — create one in the Content Library first.</div>
          ) : (
            rows.map((f) => {
              const isOpen = open.has(f.id);
              const expandable = (f.subfolder_count || 0) > 0;
              const on = sel?.id === f.id;
              const busy = loadingIds.has(f.id);
              return (
                <div key={f.id} onClick={() => setSel(f)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', paddingLeft: 10 + f.depth * 22, borderRadius: 9, cursor: 'pointer', background: on ? 'rgba(6,182,212,0.1)' : 'transparent', transition: 'background .14s' }}>
                  {expandable ? (
                    <button type="button" onClick={(e) => { e.stopPropagation(); expand(f); }} style={{ display: 'flex', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }}>
                      {busy ? <Icon name="refresh-cw" size={14} className="as-spin" /> : <Icon name="chevron-right" size={15} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />}
                    </button>
                  ) : <span style={{ width: 15, flex: 'none' }} />}
                  <Icon name={expandable && isOpen ? 'folder-open' : 'folder'} size={17} style={{ color: on ? 'var(--accent-primary-darker)' : 'var(--as-amber-600)' }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: on ? 600 : 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                  {typeof f.video_count === 'number' && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none' }}><Icon name="play-circle" size={12} />{f.video_count}</span>}
                  {on && <Icon name="check-circle" size={17} style={{ color: 'var(--accent-primary)', flex: 'none' }} />}
                </div>
              );
            })
          )}
        </div>
        <div style={{ marginTop: 14, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, background: sel ? 'rgba(6,182,212,0.06)' : 'var(--bg-tertiary)', border: `1px solid ${sel ? 'rgba(6,182,212,0.24)' : 'var(--border-color)'}` }}>
          <Icon name={sel ? 'folder' : 'info'} size={16} style={{ color: sel ? 'var(--accent-primary-darker)' : 'var(--text-tertiary)', marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
            {sel
              ? <>This files the assignment into <b style={{ color: 'var(--text-primary)' }}>{sel.name}</b> and makes it visible to that folder's students. Until saved, it stays an unfiled draft in your studio.</>
              : <>Pick a destination. Until you file it, this assignment stays an unfiled draft in your studio — students can't see it.</>}
          </p>
        </div>
      </div>
    </Modal>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Block assembly → preview markdown + small editor helpers
// ════════════════════════════════════════════════════════════════════════
const blockToMd = (b: QBlock): string => {
  if (b.type === 'text') return (b.md || '').trim();
  if (b.type === 'formula') return `$$${(b.latex || '\\,').trim()}$$`;
  if (b.type === 'figure') return b.svg || '';
  if (b.type === 'image') return b.url ? `![${(b.caption || 'figure').replace(/[\[\]]/g, '')}](${b.url})` : '';
  return '';
};
const assembleMd = (blocks: QBlock[]): string => blocks.map(blockToMd).filter(Boolean).join('\n\n');

const newBlock = (kind: QBlock['type']): QBlock => {
  const id = newBlockId();
  if (kind === 'text') return { id, type: 'text', md: '' };
  if (kind === 'formula') return { id, type: 'formula', latex: '' };
  return { id, type: 'figure', spec: { figure_type: 'other', intent: '' }, svg: null };
};

const roleCanTeach = (user: any): boolean => {
  const ms = (user?.institution_memberships || []) as any[];
  return ms.some((m) => TEACHING_ROLES.includes(m.role));
};

// ── Difficulty segmented control wrapper ────────────────────────────────────
const DifficultyPicker: React.FC<{ value: string; onChange: (p: string) => void }> = ({ value, onChange }) => (
  <Segmented options={DIFFS.map((d) => ({ value: d.preset, label: d.v }))} value={value} onChange={onChange} />
);

// ════════════════════════════════════════════════════════════════════════
//  Studio home — create panel + "Your assignments" list
// ════════════════════════════════════════════════════════════════════════
const StudioHome: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { tenant } = useTenant();
  const { showToast } = useToast();
  const institutionId = tenant!.institution_id;

  const [list, setList] = useState<AssignmentSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [preset, setPreset] = useState('balanced');
  const [syllabus, setSyllabus] = useState<string | null>(null);
  const [sources, setSources] = useState<TaggedSource[]>([]);
  const [targeting, setTargeting] = useState(true);
  const [group, setGroup] = useState<GroupOption | null>(null);
  const [reviewPlan, setReviewPlan] = useState(true);
  const [bookOpen, setBookOpen] = useState(false);
  const [lectureOpen, setLectureOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadList = useCallback(() => {
    api.get<AssignmentSummary[]>('/api/assignments')
      .then((r) => setList(r.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  // Poll the list while any row is still generating.
  useEffect(() => {
    if (!list.some((a) => ACTIVE_GEN.includes(a.generation_status))) return;
    const t = setInterval(loadList, POLL_MS);
    return () => clearInterval(t);
  }, [list, loadList]);

  const groundingRefs = useMemo(() => ({
    books: sources.filter((s) => s.kind === 'book').map((s) => s.ref),
    videos: sources.filter((s) => s.kind === 'video').map((s) => s.ref),
  }), [sources]);

  const canGenerate = (prompt.trim().length > 0 || sources.length > 0) && !generating;

  const generate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const r = await api.post('/api/assignments/generate', {
        title: title.trim() || undefined,
        source_prompt: prompt.trim() || undefined,
        difficulty_preset: preset,
        syllabus: syllabus || undefined,
        grounding_refs: groundingRefs,
        targeting_enabled: targeting,
        target_group_id: targeting && group ? group.id : undefined,
        auto_write: !reviewPlan,
      });
      navigate(`/assignment-studio/${r.data.id}`);
    } catch (e: any) {
      showToast('error', 'Could not start generation', e?.response?.data?.detail);
      setGenerating(false);
    }
  };

  const riseStyle = (i: number): React.CSSProperties => ({ animationDelay: `${i * 70}ms` });

  return (
    <div className="as-wash mx-auto max-w-5xl px-6 py-9 sm:px-8" style={{ position: 'relative' }}>
      {/* masthead */}
      <div className="as-rise" style={{ ...riseStyle(0), display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>Assignment Studio</h1>
        <span style={{ fontFamily: 'var(--as-mono)', fontSize: 11.5, color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>plan · review · write · file</span>
      </div>

      {/* CREATE PANEL — the hero artifact */}
      <div className="as-hero as-rise" style={{ ...riseStyle(1), padding: '22px 24px 20px', marginBottom: 34 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* 01 · Brief */}
          <div>
            <div className="as-sec"><span className="as-sec-num">01</span><span className="as-sec-title">Brief</span><span className="as-sec-rule" /></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name (optional — auto-named when blank)" className="as-inp" style={{ fontWeight: 600 }} />
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="What should this assignment cover? e.g. 10 numericals on projectile motion — angled launch, horizontal launch from a height, range and time of flight. Clean JEE-style values." className="as-inp" style={{ resize: 'vertical', lineHeight: 1.55 }} />
            </div>
          </div>

          {/* 02 · Ground it */}
          <div>
            <div className="as-sec"><span className="as-sec-num">02</span><span className="as-sec-title">Ground it</span><span className="as-sec-rule" /></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              {sources.map((s, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 10, background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.25)', fontSize: 12.5 }}>
                  <Icon name={s.kind === 'book' ? 'book-open' : 'play-circle'} size={13} style={{ color: 'var(--accent-primary-darker)' }} />
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.label}</span>
                  <span style={{ fontFamily: 'var(--as-mono)', fontSize: 11, color: 'var(--text-tertiary)' }}>{s.meta}</span>
                  <button type="button" onClick={() => setSources((p) => p.filter((_, j) => j !== i))} style={{ display: 'flex', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }}><Icon name="x" size={13} /></button>
                </span>
              ))}
              <button type="button" className="as-addsrc" onClick={() => setBookOpen(true)}><Icon name="book-open" size={14} /> Book chapter</button>
              <button type="button" className="as-addsrc" onClick={() => setLectureOpen(true)}><Icon name="play-circle" size={14} /> Lecture topic</button>
              {sources.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Questions stay answerable from what you tag.</span>}
            </div>
          </div>

          {/* 03 · Calibrate */}
          <div>
            <div className="as-sec"><span className="as-sec-num">03</span><span className="as-sec-title">Calibrate</span><span className="as-sec-rule" /></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginRight: 4 }}>Exam</span>
                {SYLLABI.map((s) => {
                  const on = syllabus === s.value;
                  return (
                    <button key={s.value} type="button" onClick={() => setSyllabus(on ? null : s.value)}
                      style={{ font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '6px 13px', borderRadius: 999, border: `1px solid ${on ? 'var(--accent-primary)' : 'var(--border-color)'}`, background: on ? 'rgba(6,182,212,0.1)' : 'transparent', color: on ? 'var(--accent-primary-darker)' : 'var(--text-secondary)', transition: 'all .14s' }}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Difficulty</span>
                  <DifficultyPicker value={preset} onChange={setPreset} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Target weak spots</span>
                  <Toggle on={targeting} onChange={() => setTargeting((v) => !v)} />
                  {targeting && (
                    <button type="button" className="as-addsrc" onClick={() => setBatchOpen(true)}>
                      <Icon name="users" size={14} /> {group ? group.name : 'Pick a batch'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* commit */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14, borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
              <input type="checkbox" checked={reviewPlan} onChange={(e) => setReviewPlan(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#06B6D4' }} />
              Review the plan before writing questions
            </label>
            <button type="button" className="as-genbtn" disabled={!canGenerate} onClick={generate} style={{ minWidth: 210 }}>
              {generating ? <Icon name="refresh-cw" size={16} className="as-spin" /> : <Icon name="sparkles" size={16} />}
              {generating ? 'Starting…' : 'Generate assignment'}
            </button>
          </div>
        </div>
      </div>

      {/* YOUR ASSIGNMENTS — the register */}
      <div className="as-rise" style={{ ...riseStyle(2), display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-primary)' }}>Your assignments</h2>
        {list.length > 0 && <span style={{ fontFamily: 'var(--as-mono)', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{list.length} on file</span>}
        <span className="as-sec-rule" />
      </div>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2].map((i) => <div key={i} className="as-sk" style={{ height: 58 }} />)}
        </div>
      ) : list.length === 0 ? (
        <div className="as-empty as-rise" style={riseStyle(3)}>
          <span className="as-empty-ic"><Icon name="file-pen-line" size={28} /></span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Nothing on file yet</h3>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-secondary)' }}>Generate your first assignment above — it builds in the background and lands here.</p>
        </div>
      ) : (
        <div className="as-ledger-wrap as-rise" style={riseStyle(3)}>
          {list.map((a) => (
            <button key={a.id} type="button" className="as-ledger" onClick={() => navigate(`/assignment-studio/${a.id}`)}>
              <span className="as-ledger-ref">#{String(a.ref_id).padStart(3, '0')}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 650, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                  <span>{relTime(a.updated_at)}</span>
                  {a.difficulty_preset && <span>· {presetToLabel(a.difficulty_preset)}</span>}
                  {syllabusToLabel(a.syllabus) && <span>· {syllabusToLabel(a.syllabus)}</span>}
                  {a.targeting_enabled && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>· <Icon name="target" size={11} /> targeted</span>}
                </span>
              </span>
              <AStatus kind={chipFor(a)} small />
              <Icon name="chevron-right" size={16} style={{ color: 'var(--text-tertiary)' }} />
            </button>
          ))}
        </div>
      )}

      {bookOpen && <BookPickerModal institutionId={institutionId} onClose={() => setBookOpen(false)} onAdd={(s) => setSources((p) => [...p, s])} />}
      {lectureOpen && <LecturePickerModal onClose={() => setLectureOpen(false)} onAdd={(s) => setSources((p) => [...p, s])} />}
      {batchOpen && <TargetBatchModal institutionId={institutionId} selectedId={group?.id || null} onClose={() => setBatchOpen(false)} onPick={setGroup} />}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Plan-review gate — "The Briefing": intelligence panel → question ladder
//  (read-first cards) → sticky commit bar.
// ════════════════════════════════════════════════════════════════════════

// ── ① Intelligence panel ────────────────────────────────────────────────
const SNAPSHOT_META = {
  batch: { icon: 'zap' as IconName, label: 'Targeted to your batch', tone: 'var(--as-amber-700)', sub: "Weighted to where this class actually struggled." },
  textbook_kg: { icon: 'book-open' as IconName, label: 'Textbook trouble spots', tone: 'var(--accent-primary-darker)', sub: "No batch history yet — weighted using your textbook's known trouble spots. Upgrades to real batch signal as students use Vidya." },
  none: { icon: 'target' as IconName, label: 'Covering scope evenly', tone: 'var(--text-secondary)', sub: 'No batch signal to target yet — the plan covers your prompt evenly.' },
};

const WeightRow: React.FC<{ concept: string; score: number; max: number; reason?: string; index?: number }> = ({ concept, score, max, reason, index = 0 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 24 }}>
    <span style={{ flex: '0 0 32%', minWidth: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{concept}</span>
    <span style={{ flex: '0 0 88px', height: 6, borderRadius: 999, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
      <span className="as-barfill" style={{ width: `${Math.max(8, Math.round((score / (max || 1)) * 100))}%`, animationDelay: `${150 + index * 90}ms` }} />
    </span>
    <span style={{ flex: 'none', fontFamily: 'var(--as-mono)', fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{score.toFixed(2)}</span>
    {reason && <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {reason}</span>}
  </div>
);

const IntelPanel: React.FC<{ snap?: WeaknessSnapshot | null; overview: string; onOverview: (v: string) => void }> = ({ snap, overview, onOverview }) => {
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(overview);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); autoGrow(ref.current); } }, [editing]);

  const source = (snap?.source === 'batch' || snap?.source === 'textbook_kg') ? snap.source : 'none';
  const meta = SNAPSHOT_META[source];
  const ranked = (snap?.ranked_concepts || []).filter((c) => c.concept);
  const shown = showAll ? ranked : ranked.slice(0, 3);
  const maxScore = Math.max(...ranked.map((c) => c.score ?? 0), 0.0001);

  const commit = () => { setEditing(false); if (draft !== overview) onOverview(draft); };

  return (
    <div style={{ borderRadius: 18, border: '1px solid var(--border-color)', background: 'linear-gradient(135deg, rgba(6,182,212,0.05), transparent 55%)', padding: '18px 20px', marginBottom: 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name={meta.icon} size={15} style={{ color: meta.tone }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: meta.tone }}>{meta.label}</span>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{meta.sub}</p>

      {shown.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
          {shown.map((c, i) => <WeightRow key={i} concept={c.concept} score={c.score ?? 0} max={maxScore} reason={(c.reasons || [])[0]} index={i} />)}
          {ranked.length > 3 && (
            <button type="button" onClick={() => setShowAll((v) => !v)} style={{ alignSelf: 'flex-start', font: 'inherit', fontSize: 11.5, fontWeight: 600, color: 'var(--accent-primary-darker)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {showAll ? 'show less' : `show all ${ranked.length}`} <Icon name="chevron-down" size={11} style={{ transform: showAll ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </button>
          )}
        </div>
      )}

      {/* overview — click-to-edit, same pattern as TextBlock */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--border-color)' }}>
        <span className="as-fl">Overview</span>
        {editing ? (
          <textarea ref={ref} className="as-ta" value={draft} style={{ fontSize: 13.5 }} rows={2}
            onChange={(e) => { setDraft(e.target.value); autoGrow(e.target); }}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(overview); setEditing(false); } }} />
        ) : (
          <p onClick={() => { setDraft(overview); setEditing(true); }} style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: overview ? 'var(--text-primary)' : 'var(--text-tertiary)', cursor: 'text' }}>
            {overview || 'Click to add a one-line summary of the assignment’s aim.'}
          </p>
        )}
      </div>
    </div>
  );
};

// ── ② One plan row — read-first card, expands to labeled fields ─────────
type PlanRowData = BlueprintQuestion & { _key: string };

// Auto-growing textarea — sizes to content, never clips or scrolls.
const GrowTA: React.FC<{
  value: string; onChange: (v: string) => void; placeholder?: string;
  style?: React.CSSProperties; minRows?: number; autoFocus?: boolean; onEscape?: () => void;
}> = ({ value, onChange, placeholder, style, minRows = 2, autoFocus, onEscape }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { autoGrow(ref.current); }, [value]);
  useEffect(() => { if (autoFocus && ref.current) { const el = ref.current; el.focus(); el.setSelectionRange(el.value.length, el.value.length); } }, [autoFocus]);
  return (
    <textarea
      ref={ref} value={value} rows={minRows} placeholder={placeholder} className="as-inp"
      style={{ resize: 'none', overflow: 'hidden', lineHeight: 1.6, padding: '12px 14px', ...style }}
      onChange={(e) => { onChange(e.target.value); autoGrow(e.target); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onEscape?.(); }}
    />
  );
};

// N-line clamp for long prose (expandable sections toggle it off).
const clampLines = (n: number): React.CSSProperties =>
  ({ display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' });

// One labeled section of the resting card: micro-label (with an icon accent)
// above the prose — same `as-fl` voice as the edit fields, so rest and edit read
// as the same structure.
const RestSection: React.FC<{ label: string; icon?: IconName; iconColor?: string; children: React.ReactNode }> = ({ label, icon, iconColor, children }) => (
  <div>
    <span className="as-fl" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
      {icon && <Icon name={icon} size={12} style={{ color: iconColor || 'var(--text-tertiary)' }} />}
      {label}
    </span>
    {children}
  </div>
);

const PlanRow: React.FC<{
  row: PlanRowData; index: number; editing: boolean;
  onEdit: (on: boolean) => void; onChange: (patch: Partial<BlueprintQuestion>) => void; onRemove: () => void;
}> = ({ row, index, editing, onEdit, onChange, onRemove }) => {
  const controls = useDragControls();
  const m = bandMeta(row.difficulty_band);
  const BANDS: DifficultyBand[] = ['scaffold', 'core', 'stretch'];
  return (
    // layout-only animation here: entrance is a one-shot CSS rise on the card (never
    // replays on reorder/edit re-renders — that was the jank), while `layout` keeps
    // drag-reorder shifts fluid.
    <Reorder.Item as="div" value={row} dragListener={false} dragControls={controls} layout className="list-none"
      transition={{ layout: { duration: 0.28, ease: [0.16, 1, 0.3, 1] } }}>
      <div className="as-qcard as-rise" style={{ animationDelay: `${Math.min(index * 55, 330)}ms`, padding: '15px 18px', borderLeft: `3px solid ${m.c}`, transition: 'border-color .25s ease, box-shadow .18s ease', cursor: editing ? 'auto' : 'pointer' }}
        onClick={() => !editing && onEdit(true)}>
        {/* header strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="as-grip" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => controls.start(e)} title="Drag to reorder"><Icon name="grip-vertical" size={16} /></span>
          <span className="as-qnum">{index + 1}</span>
          <BandBadge band={row.difficulty_band} />
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)' }}>{row.marks != null ? `${row.marks} marks` : 'marks TBD'}</span>
          <span style={{ flex: 1 }} />
          <IconBtn name={editing ? 'chevron-up' : 'pen-line'} title={editing ? 'Done' : 'Edit'} size={14} onClick={() => onEdit(!editing)} />
          <IconBtn name="trash-2" title="Remove question" size={14} danger onClick={onRemove} />
        </div>

        <AnimatePresence initial={false} mode="wait">
        {!editing ? (
          /* RESTING — three labeled sections, same structure the edit mode opens into */
          <motion.div key="rest" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: 'hidden' }}>
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <RestSection label="Question" icon="circle-help" iconColor="var(--accent-primary-darker)">
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.55, color: row.focus ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                {row.focus || 'Untitled question — click to describe what it tests.'}
              </p>
            </RestSection>
            <RestSection label="Reasoning — why this question" icon="zap" iconColor="var(--as-amber-600)">
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: row.rationale ? 'var(--text-secondary)' : 'var(--text-tertiary)', fontStyle: row.rationale ? 'normal' : 'italic' }}>
                {row.rationale || 'No batch rationale — this will be a generic question.'}
              </p>
            </RestSection>
            {row.figure_brief && (
              <RestSection label="Supporting diagram" icon="shapes" iconColor="var(--accent-primary-darker)">
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{row.figure_brief}</p>
              </RestSection>
            )}
            {(row.exam_refs || []).length > 0 && (
              <RestSection label="Exam precedent" icon="check-circle" iconColor="var(--as-amber-600)">
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                  Modeled on {(row.exam_refs || []).map((r, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && ' · '}
                      {r.source_url ? (
                        <a href={r.source_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--accent-primary-darker)', fontWeight: 600, textDecoration: 'none' }}>{examRefText(r)}</a>
                      ) : (
                        <b style={{ fontWeight: 600 }}>{examRefText(r)}</b>
                      )}
                    </React.Fragment>
                  ))}
                </p>
              </RestSection>
            )}
          </div>
          </motion.div>
        ) : (
          /* EDITING — labeled, auto-growing fields; nothing ever clips */
          <motion.div key="edit" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: 'hidden' }}>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 2 }} onClick={(e) => e.stopPropagation()}>
            <div>
              <span className="as-fl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="circle-help" size={12} style={{ color: 'var(--accent-primary-darker)' }} /> Question — what it tests</span>
              <GrowTA value={row.focus} onChange={(v) => onChange({ focus: v })} minRows={2} autoFocus onEscape={() => onEdit(false)}
                style={{ fontSize: 14.5, fontWeight: 600 }}
                placeholder="e.g. Projectile launched at 30° from a cliff — choosing the right kinematic axis" />
            </div>
            <div>
              <span className="as-fl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="zap" size={12} style={{ color: 'var(--as-amber-600)' }} /> Reasoning — why this question <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>(drives the writer — cite the weakness it targets)</span></span>
              <GrowTA value={row.rationale} onChange={(v) => onChange({ rationale: v })} minRows={2} onEscape={() => onEdit(false)}
                style={{ fontSize: 13.5 }}
                placeholder="e.g. targets range — typically confused with time of flight in this chapter" />
            </div>
            <div>
              <span className="as-fl" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="shapes" size={12} style={{ color: 'var(--accent-primary-darker)' }} /> Supporting diagram <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>(optional — leave blank for none)</span></span>
              <GrowTA value={row.figure_brief || ''} onChange={(v) => onChange({ figure_brief: v || null })} minRows={2} onEscape={() => onEdit(false)}
                style={{ fontSize: 13.5 }}
                placeholder="e.g. trajectory with vx/vy velocity components at apex" />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 26, flexWrap: 'wrap', paddingTop: 2 }}>
              <div>
                <span className="as-fl">Difficulty</span>
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  {BANDS.map((b) => {
                    const on = row.difficulty_band === b;
                    const bm = bandMeta(b);
                    return <button key={b} type="button" onClick={() => onChange({ difficulty_band: b })} className="as-badge" style={{ cursor: 'pointer', padding: '5px 14px', background: on ? bm.bg : 'transparent', color: on ? bm.c : 'var(--text-tertiary)', border: `1px solid ${on ? bm.bd : 'var(--border-color)'}` }}>{bm.label}</button>;
                  })}
                </span>
              </div>
              <div>
                <span className="as-fl">Marks</span>
                <MarksStepper value={row.marks ?? 3} onChange={(v) => onChange({ marks: v })} />
              </div>
              <span style={{ flex: 1 }} />
              <button type="button" onClick={() => onEdit(false)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '8px 16px', borderRadius: 10, border: '1px solid rgba(6,182,212,0.35)', background: 'rgba(6,182,212,0.08)', color: 'var(--accent-primary-darker)' }}>
                <Icon name="check" size={14} /> Done
              </button>
            </div>
          </div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>
    </Reorder.Item>
  );
};

// ── ③ The gate page ──────────────────────────────────────────────────────
const PlanGate: React.FC<{ detail: AssignmentDetail; onChanged: (d: AssignmentDetail) => void }> = ({ detail, onChanged }) => {
  const { showToast } = useToast();
  const [overview, setOverview] = useState(detail.blueprint?.overview || '');
  const [rows, setRows] = useState<PlanRowData[]>(() => (detail.blueprint?.questions || []).map((q) => ({ ...q, _key: newBlockId() })));
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);

  const setRow = (key: string, patch: Partial<BlueprintQuestion>) => setRows((p) => p.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: string) => setRows((p) => p.filter((r) => r._key !== key));
  const addRow = () => {
    if (rows.length >= MAX_QUESTIONS) return;
    const key = newBlockId();
    setRows((p) => [...p, { focus: '', rationale: '', target_concepts: [], difficulty_band: 'core', marks: 3, figure_brief: null, _key: key }]);
    setEditingKey(key);
  };

  const totalMarks = rows.reduce((s, r) => s + (r.marks ?? 0), 0);
  const bandCounts = rows.reduce<Record<string, number>>((acc, r) => { const b = r.difficulty_band || 'core'; acc[b] = (acc[b] || 0) + 1; return acc; }, {});
  const bandSummary = (['scaffold', 'core', 'stretch'] as DifficultyBand[])
    .filter((b) => bandCounts[b])
    .map((b) => `${bandCounts[b]}×${b}`)
    .join(' · ');

  const write = async () => {
    setWriting(true);
    try {
      await api.patch(`/api/assignments/${detail.id}/blueprint`, {
        overview,
        questions: rows.map((r) => ({
          focus: r.focus, rationale: r.rationale, target_concepts: r.target_concepts || [],
          difficulty_band: r.difficulty_band, marks: r.marks ?? null, figure_brief: r.figure_brief ?? null,
          exam_refs: r.exam_refs ?? null,
        })),
      });
      const r = await api.post(`/api/assignments/${detail.id}/write`);
      onChanged(r.data);
    } catch (e: any) {
      showToast('error', 'Could not start writing', e?.response?.data?.detail);
      setWriting(false);
    }
  };

  return (
    <div className="as-wash mx-auto max-w-3xl px-6 py-8 sm:px-8" style={{ paddingBottom: 0, position: 'relative' }}>
      {/* header */}
      <div className="as-rise" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="as-eyebrow">Review the plan</span>
          <span style={{ fontFamily: 'var(--as-mono)', fontSize: 10.5, color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>#{String(detail.ref_id).padStart(3, '0')}</span>
        </div>
        <h1 style={{ margin: '6px 0 0', fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>{detail.title}</h1>
        {syllabusToLabel(detail.syllabus) && (
          <span className="as-ratchip cool" style={{ marginTop: 10 }}>
            <Icon name="check-circle" size={13} /> Aligned to the {syllabusToLabel(detail.syllabus)} pattern
          </span>
        )}
        <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Edit anything below — the writer follows this plan exactly. Nothing is generated until you hit <b style={{ color: 'var(--text-primary)' }}>Write questions</b>.</p>
      </div>

      {/* ① intelligence */}
      <div className="as-rise" style={{ animationDelay: '70ms' }}>
        <IntelPanel snap={detail.batch_weakness_snapshot} overview={overview} onOverview={setOverview} />
      </div>

      {/* ② question ladder */}
      <div className="as-rise" style={{ animationDelay: '130ms', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span className="as-fl" style={{ margin: 0 }}>Planned questions</span>
        <span className="as-sec-rule" />
        <span style={{ fontFamily: 'var(--as-mono)', fontSize: 11.5, fontWeight: 600, color: rows.length >= MAX_QUESTIONS ? 'var(--as-amber-700)' : 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{rows.length}/{MAX_QUESTIONS}</span>
      </div>
      <Reorder.Group axis="y" values={rows} onReorder={setRows} as="div" style={{ display: 'flex', flexDirection: 'column', gap: 14, listStyle: 'none', padding: 0, margin: 0 }}>
        {rows.map((r, i) => (
          <PlanRow key={r._key} row={r} index={i} editing={editingKey === r._key}
            onEdit={(on) => setEditingKey(on ? r._key : null)}
            onChange={(patch) => setRow(r._key, patch)}
            onRemove={() => removeRow(r._key)} />
        ))}
      </Reorder.Group>
      {rows.length < MAX_QUESTIONS ? (
        <button type="button" className="as-addbig" onClick={addRow} style={{ marginTop: 14, width: '100%' }}><Icon name="plus" size={15} /> Add a question</button>
      ) : (
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center' }}>10-question cap per generation — remove one to add another.</p>
      )}

      {/* ③ sticky commit bar */}
      <div className="as-commit">
        <span style={{ fontFamily: 'var(--as-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {rows.length} question{rows.length === 1 ? '' : 's'} · {totalMarks} marks{bandSummary ? <span style={{ color: 'var(--text-tertiary)' }}> · {bandSummary}</span> : null}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="as-genbtn" disabled={writing || rows.length === 0} onClick={write} style={{ width: 'auto', padding: '11px 22px' }}>
          {writing ? <Icon name="refresh-cw" size={15} className="as-spin" /> : <Icon name="play" size={15} />}
          {writing ? 'Starting…' : 'Write questions'}
        </button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Question card — editable blocks + solution + regenerate
// ════════════════════════════════════════════════════════════════════════
const QuestionCard: React.FC<{
  q: AssignmentQuestion;
  index: number;
  count: number;
  assignmentId: string;
  onPatch: (q: AssignmentQuestion) => void;
  onMove: (id: string, to: number) => void;
  onSaveReport: (key: string, state: 'dirty' | 'saved' | 'error') => void;
}> = ({ q, index, count, assignmentId, onPatch, onMove, onSaveReport }) => {
  const { showToast } = useToast();
  const [solOpen, setSolOpen] = useState(false);
  const [regen, setRegen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [guidance, setGuidance] = useState('');
  const [metaOpen, setMetaOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);

  // ── Debounced, optimistic persistence ────────────────────────────────
  // Local state updates instantly (onPatch); the PATCH fires 700ms after the
  // last change and the response is NOT applied back — applying the server
  // echo mid-typing is what stole focus/caret from the block editors.
  const pendingRef = useRef<{ statement_blocks?: QBlock[]; solution_blocks?: QBlock[]; marks?: number }>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Every save chains onto the previous one. flush() therefore resolves only when
  // ALL earlier PATCHes have landed — without this, a regen fired while a debounced
  // save was still in flight overtook it server-side and 404'd on the new block.
  const inflightRef = useRef<Promise<void>>(Promise.resolve());
  const flush = useCallback((): Promise<void> => {
    const patch = pendingRef.current;
    pendingRef.current = {};
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const next = inflightRef.current.then(() => {
      if (!Object.keys(patch).length) return;
      return api.patch(`/api/assignments/${assignmentId}/questions/${q.id}`, patch)
        .then(() => onSaveReport(q.id, 'saved'))
        .catch((e: any) => { onSaveReport(q.id, 'error'); showToast('error', 'Could not save question', e?.response?.data?.detail); });
    });
    inflightRef.current = next.catch(() => {});   // chain survives failures
    return next;
  }, [assignmentId, q.id, onSaveReport, showToast]);
  const queueSave = useCallback((patch: { statement_blocks?: QBlock[]; solution_blocks?: QBlock[]; marks?: number }) => {
    Object.assign(pendingRef.current, patch);
    onSaveReport(q.id, 'dirty');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 700);
  }, [flush, onSaveReport, q.id]);
  useEffect(() => () => { flush(); }, [flush]);   // flush pending edits on unmount

  const updBlocks = (field: 'statement_blocks' | 'solution_blocks', blocks: QBlock[]) => {
    onPatch({ ...q, [field]: blocks });           // optimistic — UI is source of truth
    queueSave({ [field]: blocks } as any);
  };
  const onBlockChange = (field: 'statement_blocks' | 'solution_blocks', b: QBlock) =>
    updBlocks(field, (q[field] || []).map((x) => (x.id === b.id ? b : x)));
  const onBlockDelete = (field: 'statement_blocks' | 'solution_blocks', id: string) =>
    updBlocks(field, (q[field] || []).filter((x) => x.id !== id));
  // Image blocks need a file first — route through the hidden picker, then insert.
  const fileRef = useRef<HTMLInputElement>(null);
  const imageFieldRef = useRef<'statement_blocks' | 'solution_blocks'>('statement_blocks');
  const [uploading, setUploading] = useState(false);
  const onBlockAdd = (field: 'statement_blocks' | 'solution_blocks', kind: QBlock['type']) => {
    if (kind === 'image') {
      imageFieldRef.current = field;
      fileRef.current?.click();
      return;
    }
    updBlocks(field, [...(q[field] || []), newBlock(kind)]);
  };
  const onImagePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';                     // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post(`/api/assignments/${assignmentId}/images`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const field = imageFieldRef.current;
      updBlocks(field, [...(q[field] || []), { id: newBlockId(), type: 'image' as const, url: r.data.url, caption: '' }]);
      showToast('success', 'Image added');
    } catch (err: any) {
      showToast('error', 'Could not upload image', err?.response?.data?.detail);
    } finally { setUploading(false); }
  };
  const onBlockMove = (field: 'statement_blocks' | 'solution_blocks', id: string, dir: -1 | 1) => {
    const arr = [...(q[field] || [])];
    const i = arr.findIndex((x) => x.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    updBlocks(field, arr);
  };

  const regenDiagram = async (field: 'statement_blocks' | 'solution_blocks', blockId: string, figure_type: string, intent: string, notes?: string) => {
    // A freshly added figure block exists only locally until the debounce flushes —
    // persist first or the regen endpoint 404s the block id.
    await flush();
    try {
      const r = await api.post(`/api/assignments/${assignmentId}/questions/${q.id}/blocks/${blockId}/diagram/regenerate`, { figure_type, intent, notes });
      onPatch(r.data);
      showToast('success', 'Diagram drawn');
    } catch (e: any) {
      const detail = e?.response?.status === 404
        ? 'The block had not finished saving — wait a moment and try again.'
        : e?.response?.data?.detail;
      showToast('error', 'Could not redraw diagram', detail);
    }
    void field;
  };

  const regenerateQuestion = async () => {
    setRegenOpen(false);
    setRegen(true);
    try {
      const body = guidance.trim() ? { guidance: guidance.trim() } : {};
      const r = await api.post(`/api/assignments/${assignmentId}/questions/${q.id}/regenerate`, body);
      onPatch(r.data);
      setGuidance('');
      showToast('success', guidance.trim() ? 'Question revised' : 'Question regenerated');
    } catch (e: any) {
      showToast('error', 'Could not regenerate', e?.response?.data?.detail);
    } finally { setRegen(false); }
  };

  const renderBlocks = (field: 'statement_blocks' | 'solution_blocks') => {
    const blocks = q[field] || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {blocks.map((b, bi) => (
          <ContentBlock
            key={b.id}
            block={b}
            onChange={(nb) => onBlockChange(field, nb)}
            onDelete={() => onBlockDelete(field, b.id)}
            onRegenerate={b.type === 'figure' ? ((ft, intent, notes) => regenDiagram(field, b.id, ft, intent, notes)) : undefined}
            onMoveUp={bi > 0 ? () => onBlockMove(field, b.id, -1) : undefined}
            onMoveDown={bi < blocks.length - 1 ? () => onBlockMove(field, b.id, 1) : undefined}
          />
        ))}
        <AddBlockBar onAdd={(kind) => onBlockAdd(field, kind)} />
      </div>
    );
  };

  return (
    <div className="as-qcard" id={`as-q-${q.id}`} style={{ padding: 16 }}>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" onChange={onImagePicked} style={{ display: 'none' }} />
        {uploading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, color: 'var(--accent-primary-darker)', fontWeight: 600 }}>
            <Icon name="refresh-cw" size={13} className="as-spin" /> Uploading image…
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ position: 'relative' }}>
            <button type="button" className="as-qnum" onClick={() => setMoveOpen((v) => !v)} title="Move this question"
              style={{ border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700 }}>
              {index + 1}
            </button>
            {moveOpen && (
              <>
                <div onClick={() => setMoveOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                <div className="as-menu" style={{ top: 'calc(100% + 6px)', left: 0, width: 168 }}>
                  <button type="button" disabled={index === 0} style={{ opacity: index === 0 ? 0.45 : 1 }} onClick={() => { setMoveOpen(false); onMove(q.id, 0); }}><Icon name="chevron-up" size={14} /> To top</button>
                  <button type="button" disabled={index === 0} style={{ opacity: index === 0 ? 0.45 : 1 }} onClick={() => { setMoveOpen(false); onMove(q.id, index - 1); }}><Icon name="arrow-left" size={14} style={{ transform: 'rotate(90deg)' }} /> Up one</button>
                  <button type="button" disabled={index >= count - 1} style={{ opacity: index >= count - 1 ? 0.45 : 1 }} onClick={() => { setMoveOpen(false); onMove(q.id, index + 1); }}><Icon name="arrow-right" size={14} style={{ transform: 'rotate(90deg)' }} /> Down one</button>
                  <button type="button" disabled={index >= count - 1} style={{ opacity: index >= count - 1 ? 0.45 : 1 }} onClick={() => { setMoveOpen(false); onMove(q.id, count - 1); }}><Icon name="chevron-down" size={14} /> To bottom</button>
                </div>
              </>
            )}
          </div>
          {q.difficulty_band && <BandBadge band={q.difficulty_band} />}
          {q.solution_check?.status === 'verified' && (
            <span className="as-badge" title={`Independently re-solved with a symbolic solver — answer agrees (${q.solution_check.stated ?? ''})`} style={{ background: 'rgba(6,182,212,0.1)', color: 'var(--accent-primary-darker)', border: '1px solid rgba(6,182,212,0.28)' }}>
              <Icon name="check-circle" size={12} /> Solution verified
            </span>
          )}
          {q.solution_check?.status === 'mismatch' && (
            <span className="as-badge" title={`Symbolic solver computed ${q.solution_check.computed ?? '?'} but the solution states ${q.solution_check.stated ?? '?'} — check the answer key before sharing.`} style={{ background: 'rgba(220,38,38,0.1)', color: 'var(--as-danger)', border: '1px solid rgba(220,38,38,0.3)' }}>
              <Icon name="alert-triangle" size={12} /> Check answer
            </span>
          )}
          <span style={{ flex: 1 }} />
          <MarksStepper value={q.marks ?? 3} onChange={(v) => { onPatch({ ...q, marks: v }); queueSave({ marks: v }); }} />
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setRegenOpen((v) => !v)} disabled={regen} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12, fontWeight: 600, cursor: regen ? 'default' : 'pointer', padding: '6px 10px', borderRadius: 9, border: '1px solid rgba(6,182,212,0.3)', background: 'rgba(6,182,212,0.06)', color: 'var(--accent-primary-darker)' }}>
              <Icon name="refresh-cw" size={13} className={regen ? 'as-spin' : ''} /> {regen ? 'Regenerating…' : 'Regenerate'}
            </button>
            {regenOpen && !regen && (
              <>
                <div onClick={() => setRegenOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 31, width: 340, padding: 14, borderRadius: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', boxShadow: '0 18px 44px -16px rgba(15,23,42,0.3)' }}>
                  <span className="as-fl">Regenerate this question</span>
                  <GrowTA value={guidance} onChange={setGuidance} minRows={2} autoFocus onEscape={() => setRegenOpen(false)}
                    style={{ fontSize: 13 }}
                    placeholder="What should change? e.g. easier numbers, same scenario · blank = fresh re-roll" />
                  <p style={{ margin: '8px 0 10px', fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-tertiary)' }}>
                    Diagram only? Edit its description under the figure block instead — keeps the question intact.
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button type="button" onClick={() => setRegenOpen(false)} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '7px 13px', borderRadius: 9, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)' }}>Cancel</button>
                    <button type="button" onClick={regenerateQuestion} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '7px 14px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg, #06B6D4, #2563EB)', color: '#fff' }}>
                      <Icon name="refresh-cw" size={13} /> {guidance.trim() ? 'Revise' : 'Re-roll'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* provenance: seed + reasoning + exam precedent — labeled, clamped, expandable */}
        {(q.focus || q.rationale || (q.exam_refs || []).length > 0) && (
          <div
            onClick={() => setMetaOpen((v) => !v)}
            title={metaOpen ? 'Collapse' : 'Expand'}
            style={{ marginBottom: 14, padding: '11px 13px', borderRadius: 11, background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 9, cursor: 'pointer' }}
          >
            {q.focus && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Icon name="target" size={12} style={{ marginTop: 3, flex: 'none', color: 'var(--accent-primary-darker)' }} />
                <span style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)', ...(metaOpen ? {} : clampLines(1)) }}>
                  <b style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Tests:</b> {q.focus}
                </span>
              </div>
            )}
            {q.rationale && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Icon name="zap" size={12} style={{ marginTop: 3, flex: 'none', color: 'var(--as-amber-600)' }} />
                <span style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)', ...(metaOpen ? {} : clampLines(1)) }}>
                  <b style={{ fontWeight: 700, color: 'var(--as-amber-700)' }}>Why:</b> {q.rationale}
                </span>
              </div>
            )}
            {(q.exam_refs || []).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Icon name="check-circle" size={12} style={{ marginTop: 3, flex: 'none', color: 'var(--as-amber-600)' }} />
                <span style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                  <b style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Precedent:</b>{' '}
                  {(q.exam_refs || []).map((r, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && ' · '}
                      {r.source_url
                        ? <a href={r.source_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--accent-primary-darker)', fontWeight: 600, textDecoration: 'none' }}>{examRefText(r)}</a>
                        : <span style={{ fontWeight: 600 }}>{examRefText(r)}</span>}
                    </React.Fragment>
                  ))}
                </span>
              </div>
            )}
          </div>
        )}

        {renderBlocks('statement_blocks')}

        {/* solution (answer key) */}
        <div className="as-solwrap" style={{ marginTop: 12 }}>
          <button type="button" className="as-solhead" onClick={() => setSolOpen((v) => !v)}>
            <Icon name="check-circle" size={15} style={{ color: 'var(--as-amber-600)' }} />
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--as-amber-700)' }}>Model solution</span>
            <span style={{ flex: 1 }} />
            <Icon name="chevron-down" size={15} style={{ color: 'var(--as-amber-600)', transform: solOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
          </button>
          <AnimatePresence initial={false}>
            {solOpen && (
              <motion.div key="sol" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: 'hidden' }}>
                <div style={{ padding: '2px 13px 13px' }}>{renderBlocks('solution_blocks')}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Live preview sheet (student-facing assembly)
// ════════════════════════════════════════════════════════════════════════
const PreviewSheet: React.FC<{ detail: AssignmentDetail; showSolutions: boolean }> = ({ detail, showSolutions }) => {
  // Click-to-expand for uploaded images on the sheet — same affordance the
  // vidya-svg figures ship built-in.
  const [lightbox, setLightbox] = useState<string | null>(null);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);
  return (
  <div className="as-sheet" style={{ padding: '28px 30px', position: 'relative' }}>
    {showSolutions && <span className="as-anskey">ANSWER KEY</span>}
    <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800 }}>{detail.title}</h2>
    {detail.created_by_name && (
      <p style={{ margin: '0 0 20px', fontSize: 12.5, color: '#6B7280' }}>
        Prepared by {detail.created_by_name}{detail.target_group_name ? ` for ${detail.target_group_name}` : ''}
        {detail.targeting_enabled && detail.batch_weakness_snapshot?.source !== 'none' ? ' — targeting the topics this class found hardest.' : ''}
      </p>
    )}
    {/* Render in the order GIVEN — the editor passes its live array, so drag-reorder
        reflects instantly (sorting by stale order_index here is what froze the preview). */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {detail.questions.map((q, i) => (
        <div key={q.id}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 800 }}>Q{i + 1}.</span>
            {q.marks != null && <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 'auto' }}>[{q.marks} marks]</span>}
          </div>
          <MarkdownRenderer content={assembleMd(q.statement_blocks || [])} onImageClick={setLightbox} />
          <AnimatePresence initial={false}>
            {showSolutions && (q.solution_blocks || []).length > 0 && (
              <motion.div
                key="sol"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                style={{ overflow: 'hidden' }}
              >
                <div style={{ marginTop: 10, paddingLeft: 14, borderLeft: '3px solid #D88B30' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#B36F1F', marginBottom: 6 }}>Solution</div>
                  <MarkdownRenderer content={assembleMd(q.solution_blocks || [])} onImageClick={setLightbox} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
    {lightbox && createPortal(
      <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, background: 'rgba(3,7,18,0.82)', backdropFilter: 'blur(4px)', cursor: 'zoom-out' }}>
        <img src={lightbox} alt="" style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 10, boxShadow: '0 30px 90px -20px rgba(0,0,0,0.8)' }} />
      </div>,
      document.body
    )}
  </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Editor — ready assignment: question list + live preview
// ════════════════════════════════════════════════════════════════════════
const ReadyEditor: React.FC<{ detail: AssignmentDetail; onChanged: (d: AssignmentDetail) => void }> = ({ detail, onChanged }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [questions, setQuestions] = useState<AssignmentQuestion[]>(() => [...detail.questions].sort((a, b) => a.order_index - b.order_index));
  const [showPreview, setShowPreview] = useState(true);
  const [showSolutions, setShowSolutions] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState(detail.title);
  const [saveOpen, setSaveOpen] = useState(false);

  useEffect(() => { setQuestions([...detail.questions].sort((a, b) => a.order_index - b.order_index)); }, [detail.questions]);

  // Ref mirror — drag/move handlers read the freshest array, never a stale closure.
  const questionsRef = useRef<AssignmentQuestion[]>(questions);
  questionsRef.current = questions;
  const editorColRef = useRef<HTMLDivElement>(null);

  const patchQuestion = useCallback((nq: AssignmentQuestion) => {
    setQuestions((p) => p.map((x) => (x.id === nq.id ? { ...x, ...nq } : x)));
  }, []);

  // ── Global save ledger — every mutation source reports here; the sticky header
  //    shows ONE persistent truth ("All changes saved" / "Saving…" / failure+retry).
  //    Per-card transient chips were invisible on tall cards scrolled past the fold.
  const [saveMap, setSaveMap] = useState<Record<string, 'dirty' | 'saved' | 'error'>>({});
  const reportSave = useCallback((key: string, state: 'dirty' | 'saved' | 'error') => {
    setSaveMap((m) => (m[key] === state ? m : { ...m, [key]: state }));
  }, []);
  const saveAgg: 'saved' | 'saving' | 'error' = Object.values(saveMap).includes('error')
    ? 'error' : Object.values(saveMap).includes('dirty') ? 'saving' : 'saved';

  const persistOrder = useCallback(async (ordered: AssignmentQuestion[]) => {
    reportSave('order', 'dirty');
    try {
      await api.patch(`/api/assignments/${detail.id}/questions/reorder`, {
        order: ordered.map((q, i) => ({ id: q.id, order_index: i })),
      });
      reportSave('order', 'saved');
    } catch {
      reportSave('order', 'error');
      showToast('error', 'Could not save the new order');
    }
  }, [detail.id, reportSave, showToast]);

  // Positional move — the long-list path drag can't serve (no auto-scroll past the fold).
  const moveQuestion = useCallback((id: string, to: number) => {
    const arr = [...questionsRef.current];
    const from = arr.findIndex((x) => x.id === id);
    if (from < 0) return;
    const clamped = Math.max(0, Math.min(arr.length - 1, to));
    if (clamped === from) return;
    const [item] = arr.splice(from, 1);
    arr.splice(clamped, 0, item);
    const remapped = arr.map((q, i) => ({ ...q, order_index: i }));
    questionsRef.current = remapped;     // keep the mirror honest before the re-render
    setQuestions(remapped);
    void persistOrder(remapped);
    // Scroll ONLY the editor column to the moved card — scrollIntoView walks every
    // ancestor and was dragging the page shell (hiding the app chrome + header).
    requestAnimationFrame(() => {
      const col = editorColRef.current;
      const el = document.getElementById(`as-q-${id}`);
      if (col && el) {
        const top = el.getBoundingClientRect().top - col.getBoundingClientRect().top + col.scrollTop;
        col.scrollTo({ top: Math.max(0, top - 90), behavior: 'smooth' });
      }
    });
  }, [persistOrder]);

  const rename = async () => {
    setRenaming(false);
    const t = titleDraft.trim();
    if (!t || t === detail.title) { setTitleDraft(detail.title); return; }
    try { const r = await api.patch(`/api/assignments/${detail.id}`, { title: t }); onChanged(r.data); }
    catch (e: any) { showToast('error', 'Could not rename', e?.response?.data?.detail); }
  };

  const saveToDrive = async (folderId: string, name: string) => {
    try {
      const r = await api.patch(`/api/assignments/${detail.id}`, { folder_id: folderId, status: 'saved' });
      onChanged(r.data);
      showToast('success', `Saved to ${name}`);
    } catch (e: any) { showToast('error', 'Could not save to Library', e?.response?.data?.detail); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* header — sticky so no scroll (inner or outer) can hide the controls/save state */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 22px', borderBottom: '1px solid var(--border-color)', flex: 'none', position: 'sticky', top: 0, zIndex: 25, background: 'var(--bg-primary)' }}>
        <IconBtn name="arrow-left" title="Back to studio" onClick={() => navigate('/assignment-studio')} />
        {renaming ? (
          <input autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} onBlur={rename} onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') { setTitleDraft(detail.title); setRenaming(false); } }} className="as-inp" style={{ maxWidth: 380, fontSize: 16, fontWeight: 700 }} />
        ) : (
          <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <button type="button" onClick={() => { setTitleDraft(detail.title); setRenaming(true); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)', padding: 0, minWidth: 0 }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{detail.title}</span>
              <Icon name="pen-line" size={13} style={{ color: 'var(--text-tertiary)', flex: 'none' }} />
            </button>
            <span style={{ fontFamily: 'var(--as-mono)', fontSize: 10.5, color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>
              #{String(detail.ref_id).padStart(3, '0')}{syllabusToLabel(detail.syllabus) ? ` · ${syllabusToLabel(detail.syllabus)}` : ''} · {detail.questions.length} questions
            </span>
          </span>
        )}
        <AStatus kind={chipFor(detail)} />
        <span style={{ flex: 1 }} />
        {saveAgg === 'error' ? (
          <button type="button" onClick={() => { if (saveMap.order === 'error') persistOrder(questionsRef.current); }} title="Some changes failed to save — edits retry automatically when you change them again; click to retry the question order." style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '6px 12px', borderRadius: 9, border: '1px solid rgba(220,38,38,0.4)', background: 'rgba(220,38,38,0.08)', color: 'var(--as-danger)' }}>
            <Icon name="alert-triangle" size={13} /> Not all changes saved — Retry
          </button>
        ) : (
          <span title="Every edit autosaves a moment after you finish it." style={{ fontFamily: 'var(--as-mono)', fontSize: 11, letterSpacing: '0.06em', color: saveAgg === 'saved' ? 'var(--text-tertiary)' : 'var(--accent-primary-darker)', display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none' }}>
            {saveAgg === 'saved'
              ? <><Icon name="check-circle" size={12} /> All changes saved</>
              : <><Icon name="refresh-cw" size={12} className="as-spin" /> Saving…</>}
          </span>
        )}
        <Segmented size="sm" options={[{ value: 'edit', label: 'Edit', icon: 'pen-tool' }, { value: 'preview', label: 'Preview', icon: 'eye' }]} value={showPreview ? 'preview' : 'edit'} onChange={(v) => setShowPreview(v === 'preview')} />
        <button type="button" className="as-genbtn" onClick={() => setSaveOpen(true)} style={{ width: 'auto', padding: '9px 16px', fontSize: 13 }}>
          <Icon name="hard-drive" size={15} /> Save to Library
        </button>
      </div>

      {/* body */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* editor column */}
        <div ref={editorColRef} style={{ flex: showPreview ? '0 0 52%' : '1 1 100%', minWidth: 0, overflowY: 'auto', padding: '20px 22px' }}>
          {questions.length === 0 ? (
            <div className="as-empty"><span className="as-empty-ic"><Icon name="file-pen-line" size={28} /></span><p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>No questions in this assignment.</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {questions.map((q, i) => (
                <QuestionCard key={q.id} q={q} index={i} count={questions.length} assignmentId={detail.id}
                  onPatch={patchQuestion} onMove={moveQuestion} onSaveReport={reportSave} />
              ))}
            </div>
          )}
        </div>

        {/* preview column */}
        {showPreview && (
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '20px 22px', borderLeft: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span className="as-fl" style={{ margin: 0 }}>Student preview</span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 600 }}>
                <Toggle on={showSolutions} onChange={() => setShowSolutions((v) => !v)} /> Solutions
              </label>
            </div>
            <PreviewSheet detail={{ ...detail, questions }} showSolutions={showSolutions} />
          </div>
        )}
      </div>

      {saveOpen && <SaveToDriveModal onClose={() => setSaveOpen(false)} onSave={saveToDrive} />}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Generation progress + failure states
// ════════════════════════════════════════════════════════════════════════
const ProgressView: React.FC<{ detail: AssignmentDetail }> = ({ detail }) => {
  const navigate = useNavigate();
  const planned = detail.blueprint?.questions?.length || 0;
  const written = detail.questions.length;
  const stage = detail.generation_status;
  const label = stage === 'writing'
    ? `Writing questions${planned ? ` — ${written} of ${planned} done` : '…'}`
    : stage === 'planning' ? 'Reading your sources & planning the paper…' : 'Starting up…';
  const STAGES: { key: GenerationStatus[]; label: string }[] = [
    { key: ['pending', 'planning'], label: 'plan' },
    { key: ['plan_ready'], label: 'review' },
    { key: ['writing'], label: 'write' },
    { key: ['ready'], label: 'ready' },
  ];
  return (
    <div className="as-wash mx-auto max-w-2xl px-6 py-16 text-center" style={{ position: 'relative' }}>
      <button type="button" onClick={() => navigate('/assignment-studio')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 34 }}><Icon name="arrow-left" size={15} /> Studio</button>
      <div className="as-rings as-rise" style={{ marginBottom: 26 }}>
        <span /><span /><span />
        <i><Icon name="file-pen-line" size={20} /></i>
      </div>
      <h2 className="as-rise" style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)', animationDelay: '80ms' }}>{detail.title}</h2>
      <p className="as-rise" style={{ margin: '12px 0 0', fontSize: 14, fontWeight: 600, color: 'var(--accent-primary-darker)', animationDelay: '140ms' }}>{label}</p>
      {stage === 'writing' && planned > 0 && (
        <div className="as-rise" style={{ margin: '16px auto 0', maxWidth: 260, height: 4, borderRadius: 999, background: 'var(--bg-tertiary)', overflow: 'hidden', animationDelay: '180ms' }}>
          <div style={{ height: '100%', width: `${Math.max(6, Math.round((written / planned) * 100))}%`, borderRadius: 999, background: 'linear-gradient(90deg, #06B6D4, #2563EB)', transition: 'width .6s ease' }} />
        </div>
      )}
      <div className="as-stages as-rise" style={{ marginTop: 22, animationDelay: '220ms' }}>
        {STAGES.map((s, i) => (
          <React.Fragment key={s.label}>
            {i > 0 && <i>→</i>}
            {s.key.includes(stage) ? <b>{s.label}</b> : <span>{s.label}</span>}
          </React.Fragment>
        ))}
      </div>
      <p className="as-rise" style={{ margin: '22px 0 0', fontSize: 12.5, color: 'var(--text-tertiary)', animationDelay: '260ms' }}>Safe to leave — it keeps building in the background. Resume anytime from your studio list.</p>
    </div>
  );
};

const FailedView: React.FC<{ detail: AssignmentDetail; onChanged: (d: AssignmentDetail) => void }> = ({ detail, onChanged }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [retrying, setRetrying] = useState(false);
  const retry = async () => {
    setRetrying(true);
    try { const r = await api.post(`/api/assignments/${detail.id}/retry`); onChanged(r.data); }
    catch (e: any) { showToast('error', 'Could not retry', e?.response?.data?.detail); setRetrying(false); }
  };
  return (
    <div className="mx-auto max-w-2xl px-6 py-20 text-center">
      <button type="button" onClick={() => navigate('/assignment-studio')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 30 }}><Icon name="arrow-left" size={15} /> Studio</button>
      <span className="as-empty-ic as-rise" style={{ margin: '0 auto 20px', background: 'rgba(220,38,38,0.1)', color: 'var(--as-danger)' }}><Icon name="alert-triangle" size={28} /></span>
      <h2 className="as-rise" style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)', animationDelay: '60ms' }}>Generation hit a wall</h2>
      <p className="as-rise" style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)', animationDelay: '100ms' }}>Your plan and any finished questions are safe — retry resumes from where it stopped.</p>
      {detail.generation_error && (
        <p className="as-rise" style={{ margin: '14px auto 0', maxWidth: 460, fontSize: 12, fontFamily: 'var(--as-mono)', color: 'var(--text-tertiary)', lineHeight: 1.6, background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '10px 14px', textAlign: 'left', animationDelay: '140ms' }}>{detail.generation_error}</p>
      )}
      <button type="button" className="as-genbtn as-rise" disabled={retrying} onClick={retry} style={{ width: 'auto', padding: '11px 22px', margin: '24px auto 0', animationDelay: '180ms' }}>
        {retrying ? <Icon name="refresh-cw" size={15} className="as-spin" /> : <Icon name="refresh-cw" size={15} />} Resume generation
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Editor route — load detail, poll, branch on generation_status
// ════════════════════════════════════════════════════════════════════════
const AssignmentEditor: React.FC<{ id: string }> = ({ id }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [detail, setDetail] = useState<AssignmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get<AssignmentDetail>(`/api/assignments/${id}`); setDetail(r.data); }
    catch { setMissing(true); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { setLoading(true); setMissing(false); load(); }, [id, load]);

  // Poll while a generation stage is active.
  useEffect(() => {
    if (!detail || !ACTIVE_GEN.includes(detail.generation_status)) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [detail, load]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div>;
  if (missing || !detail) {
    return (
      <div className="mx-auto max-w-xl px-6 py-20 text-center">
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Assignment not found</h2>
        <button type="button" className="as-addbig" onClick={() => navigate('/assignment-studio')} style={{ marginTop: 16 }}><Icon name="arrow-left" size={15} /> Back to studio</button>
      </div>
    );
  }
  const s = detail.generation_status;
  if (s === 'failed') return <FailedView detail={detail} onChanged={setDetail} />;
  if (ACTIVE_GEN.includes(s)) return <ProgressView detail={detail} />;
  if (s === 'plan_ready') return <PlanGate detail={detail} onChanged={setDetail} />;
  return <ReadyEditor detail={detail} onChanged={setDetail} />;
};

// ════════════════════════════════════════════════════════════════════════
//  No-institution notice
// ════════════════════════════════════════════════════════════════════════
const NoInstitutionNotice: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const seen = new Set<string>();
  const targets = ((user?.institution_memberships || []) as any[])
    .filter((m) => TEACHING_ROLES.includes(m.role) && m.institution_slug && !seen.has(m.institution_slug) && seen.add(m.institution_slug))
    .map((m) => ({ slug: m.institution_slug as string, name: (m.institution_name as string) || m.institution_slug }));
  return (
    <div className="mx-auto max-w-xl px-8 py-20 text-center">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><BuildingOffice2Icon className="h-7 w-7" /></span>
      <h2 className={cx('mt-5 text-2xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-gray-900')}>Open Assignment Studio on your institution</h2>
      <p className={cx('mx-auto mt-2 max-w-md text-sm leading-relaxed', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Assignments are scoped to an institution, so the studio runs on your institution's space — not the main site.</p>
      {targets.length > 0 && (
        <div className="mt-6 flex flex-col items-center gap-2">
          {targets.map((t) => (
            <a key={t.slug} href={buildTenantUrl(t.slug, '/assignment-studio') || '#'} className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-cyan-600">Go to {t.name} <ArrowRightIcon className="h-4 w-4" /></a>
          ))}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Fullscreen viewer — the reading room. Read-first surface opened from the
//  Content Library; editing lives one deliberate click away in the studio.
// ════════════════════════════════════════════════════════════════════════
export const AssignmentViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [detail, setDetail] = useState<AssignmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [showSolutions, setShowSolutions] = useState(false);

  useEffect(() => {
    if (!id || !tenant?.institution_id) return;
    let alive = true;
    setLoading(true); setMissing(false);
    api.get<AssignmentDetail>(`/api/assignments/${id}`)
      .then((r) => { if (alive) setDetail(r.data); })
      .catch(() => alive && setMissing(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, tenant?.institution_id]);

  if (!tenant?.institution_id) return <Layout><NoInstitutionNotice /></Layout>;

  const canEdit = roleCanTeach(user);
  const hasSolutions = !!detail?.questions.some((q) => (q.solution_blocks || []).length > 0);

  return (
    <Layout>
      <div className="as-wash" style={{ position: 'relative', minHeight: 'calc(100vh - 64px)' }}>
        {/* toolbar — slim, sticky, stays out of the sheet's way */}
        <div style={{ position: 'sticky', top: 0, zIndex: 25, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 22px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-primary)' }}>
          <IconBtn name="arrow-left" title="Back to Content Library" onClick={() => navigate('/video-library')} />
          <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{detail?.title || 'Assignment'}</span>
            {detail && (
              <span style={{ fontFamily: 'var(--as-mono)', fontSize: 10.5, color: 'var(--text-tertiary)', letterSpacing: '0.05em' }}>
                #{String(detail.ref_id).padStart(3, '0')}{syllabusToLabel(detail.syllabus) ? ` · ${syllabusToLabel(detail.syllabus)}` : ''} · {detail.questions.length} questions
              </span>
            )}
          </span>
          <span style={{ flex: 1 }} />
          {hasSolutions && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 600, flex: 'none' }}>
              <Toggle on={showSolutions} onChange={() => setShowSolutions((v) => !v)} /> Solutions
            </label>
          )}
          {canEdit && detail && (
            <button type="button" onClick={() => navigate(`/assignment-studio/${detail.id}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '8px 14px', borderRadius: 10, border: '1px solid rgba(6,182,212,0.35)', background: 'rgba(6,182,212,0.08)', color: 'var(--accent-primary-darker)', flex: 'none' }}>
              <Icon name="pen-tool" size={13} /> Edit in studio
            </button>
          )}
        </div>

        {/* the sheet */}
        <div style={{ maxWidth: 880, margin: '0 auto', padding: '26px 22px 56px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div>
          ) : missing || !detail ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <h2 style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-primary)' }}>Assignment not found</h2>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>It may not be shared with you, or it was removed.</p>
            </div>
          ) : (
            <div className="as-rise">
              {ACTIVE_GEN.includes(detail.generation_status) && (
                <div className={cx('as-ratchip cool')} style={{ marginBottom: 14 }}>
                  <Icon name="refresh-cw" size={12} className="as-spin" /> Still generating — showing the questions written so far.
                </div>
              )}
              <PreviewSheet detail={detail} showSolutions={showSolutions && hasSolutions} />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

// ════════════════════════════════════════════════════════════════════════
//  Route entry
// ════════════════════════════════════════════════════════════════════════
const AssignmentStudio: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const body = !tenant?.institution_id
    ? <NoInstitutionNotice />
    : !roleCanTeach(user)
      ? <NoInstitutionNotice />
      : id
        ? <AssignmentEditor id={id} />
        : <StudioHome />;
  return <Layout>{body}</Layout>;
};

export default AssignmentStudio;
